// All human-visible strings of the site. To add a locale, create a file with the
// same keys (type Dict) and register it in ./index.ts. `{name}` marks a parameter.
export const en = {
  "meta.title": "V-Kit · Marketing Kit: Ad & App Data in Claude and ChatGPT",
  "meta.description": "Ask Claude or ChatGPT about Meta Ads, Google Ads, GA4, AppsFlyer, RevenueCat and App Store Connect. Read-only, keys encrypted, data fetched, not stored.",
  // Where the price is said (ADR-0045): price.* with MK_PRICING_PUBLIC=on, priceFree.* (the ADR-0044 wording) with
  // off (ADR-0048). Read them through priceText() in ../price.ts - the one place that picks the pair.
  "price.short": "€19.99/month or €199/year · 30 days free, no card",
  "price.pillTrial": "30 days free",
  "price.pillRest": "then €19.99/mo or €199/yr · no card",
  "price.long": "€19.99 a month or €199 a year, VAT included where it applies; 30 days free without a card; cancel any time; refund within 14 days of any charge (Terms, section 7).",
  "price.monthly": "€19.99/month",
  "price.yearly": "€199/year",
  "price.row": "€19.99/month or €199/year, VAT included where it applies; 30 days free, no card; refund within 14 days of any charge (Terms, section 7)",
  "price.faqQ": "How much does V-Kit cost?",
  // {clients} - countWord(CLIENTS_DEFAULT_MAX), filled by priceText() (ADR-0062: up to three clients in the same price; BL-194).
  "price.faqA": "€19.99 a month or €199 a year, VAT included where it applies, with every source and assistant and up to {clients} clients included. The first 30 days are free without a card; cancel any time, and any charge is refunded within 14 days ([Terms](/terms.html), section 7; [Pricing](/pricing)).",
  "priceFree.short": "Free",
  "priceFree.long": "Free; any pricing will be announced in advance.",
  "priceFree.row": "Free; any pricing will be announced in advance (Terms, section 7)",
  "priceFree.faqQ": "Is V-Kit free?",
  "priceFree.faqA": "Free. Any pricing will be announced in advance and applies only after you agree to it ([Terms](/terms.html), section 7).",
  "meta.title.install": "Install V-Kit in Claude or ChatGPT",
  "meta.description.install": "Add the V-Kit plugin to Claude or ChatGPT in a few steps, then connect your ad and app sources. Read-only access.",
  "meta.title.support": "Support: Help and Contact | V-Kit",
  "meta.description.support": "Get help with V-Kit: write to the developer at connect@valas.team, open the setup guide, or send a note from your account.",
  "meta.title.guide": "Setup Guide: Install & Connect Sources | V-Kit",
  "meta.description.guide": "Step-by-step setup: install V-Kit in Claude, Claude Code or ChatGPT, then connect Meta Ads, Google Ads, GA4, AppsFlyer, RevenueCat, App Store Connect.",
  "meta.title.connections": "Connections · V-Kit",
  "meta.title.preview": "Data preview · V-Kit",
  "meta.title.account": "Account · V-Kit",
  "meta.title.consent": "Allow access · V-Kit",
  "meta.title.signin": "Sign in · V-Kit",
  "brand": "V-Kit",
  "brand.second": "Marketing Kit",

  "nav.connections": "Connections",
  "nav.install": "Install",
  "nav.installPublic": "Install in Claude or ChatGPT",
  "nav.account": "Account",
  "nav.signin": "Sign in",
  "nav.preview": "Data preview",
  "nav.pricing": "Pricing",
  "menu.open": "Account menu",

  "common.disconnect": "Disconnect",
  "common.save": "Save",
  "common.none": "-",
  "common.cancel": "Cancel",
  "common.copy": "Copy",
  "common.copied": "Copied",

  "login.with": "Sign in with {provider}",

  "landing.installCta": "Install V-Kit",
  "landing.connectCta": "Connect {source}",
  "landing.sources": "Sources",
  "landing.faq": "Questions",
  "landing.related": "Related",
  "landing.back": "← {name}",
  "landing.checked": "Facts checked on {date}",
  "landing.guide": "Setup guide for {source}",
  "landing.guideAll": "Setup guide: install and connect every source",
  "landing.privacy": "Read-only access, keys encrypted, your data fetched and not stored.",
  "landing.privacyLink": "Privacy policy",

  "home.kicker": "A plugin for Claude and ChatGPT",
  "home.title": "Your marketing numbers, answered in plain words",
  "home.subtitle":
    "A plugin for Claude and ChatGPT: Meta Ads, Google Ads, GA4, Search Console, TikTok Ads, Apple Ads, AppsFlyer, RevenueCat, App Store Connect, Google Play and the App Store. Ask your assistant; it reads only the accounts you connect and public App Store data.",
  "home.openConnections": "Open Connections",
  "home.hint": "Sign-in only identifies you. Access to each source is granted separately, by you, on the Connections page.",
  "home.example.label": "Example conversation",
  "home.example.question": "How did paid do last week?",
  "home.example.answer": "Spend up 8%, installs up 21% - Meta carried it. Cost per trial fell to",
  "home.example.colSource": "Source",
  "home.example.colSpend": "Spend",
  "home.example.colInstalls": "Installs",
  "home.example.foot": "Illustrative example · real answers are read from your own accounts when you ask",
  "home.signedAs": "Signed in as",
  "home.seeNumbers": "See your numbers",
  "home.addSource": "Add a source",
  "home.health.title": "Health status",
  "home.health.live": "{n} sources live",
  "home.health.live1": "1 source live",
  // Blocked plan (expired, paused; ADR-0045): the data of the sources is not read, so nothing is called «live».
  "home.health.blockedSubscribe": "Answers paused - subscribe to resume",
  "home.health.blockedCard": "Answers paused - update your card to resume",
  "home.health.needsLook": "Needs a look",
  "home.health.permissions": "permissions",
  "home.health.via.google": "via Google",
  "home.health.via.meta": "via Meta",
  "home.health.via.tiktok": "via TikTok",
  "home.health.via.google_play": "via Google Play",
  "home.health.via.apple_ads": "via Apple",
  "home.health.reports": "reports",
  "home.health.secretKey": "secret key",
  "home.health.teamKey": "team key",
  "home.health.public": "public data",
  "home.health.askingFrom": "Asking from",
  "home.health.lastRequest": "last request",
  "home.health.oneClient": "{client} · {kind} connected · last request {when}",
  "home.health.never": "no requests yet",
  "home.health.noClient": "No assistant connected yet.",
  "home.health.install": "Install",
  "home.health.kind.connector": "connector",
  "home.health.kind.gpt": "GPT",
  "home.health.kind.extension": "extension",
  "home.stepsTitle": "How it works",
  "home.step1.title": "Sign in",
  "home.step1.text": "With Google or Meta. No password of yours is ever seen by this site.",
  "home.step2.title": "Connect your sources",
  "home.step2.text": "Authorize Google, Meta and TikTok in their own dialogs, and Google Play and Apple Ads with their own “Connect” buttons; save the keys for AppsFlyer, RevenueCat and App Store Connect.",
  "home.step3.title": "Ask",
  "home.step3.text": "Add V-Kit to Claude or ChatGPT, then ask about spend, installs, MRR or reviews. A good first message: “check v-marketing-kit status”.",
  "home.sourcesTitle": "Thirteen sources, one place",
  "home.sourcesLead": "Each one opens its step-by-step instruction. Five sign-ins - Google, Meta, TikTok, a separate one for Google Play and Apple for Apple Ads - and three keys cover twelve of them; public App Store data needs neither.",
  "home.group.paid": "Paid",
  "home.group.organic": "Organic",
  "home.group.product": "Product",
  "home.group.stores": "App stores",
  "home.privacy.kicker": "Private by design",
  "home.privacy.title": "We don't keep your numbers. We help you fetch them.",
  "home.privacy.lead": "V-Kit sits between your assistant and your accounts. It holds the access you grant, encrypted, and nothing of what comes back.",
  "home.privacy.readonly.title": "Read-only",
  "home.privacy.readonly.text": "Every tool reads. The two exceptions - copying AppsFlyer partner-integration settings and turning on App Store Connect analytics - first show what will change and run only after your explicit yes.",
  "home.privacy.keys.title": "Keys encrypted",
  "home.privacy.keys.text": "Tokens and keys are encrypted at rest and never shown back. The App Store Connect .p8 is uploaded here once and never leaves the server.",
  "home.privacy.nothing.title": "Nothing stored",
  "home.privacy.nothing.text": "Your numbers are fetched when you ask and handed to the assistant. Only a result too large for the chat waits behind a download link for up to 24 hours, and a request log is kept without secrets.",
  "home.privacy.revoke.title": "Yours to revoke",
  "home.privacy.revoke.text": "Disconnect a source, remove a key or delete the account at any moment. Revoking in Google, Meta or TikTok works too - the next request notices and drops the connection.",
  "home.privacy.policy": "Privacy policy",

  "msg.providerNotConfiguredLogin": "Sign-in with {provider} is not set up by the site owner yet",
  "msg.appNotConfiguredLogin": "The app is not set up by the site owner yet",
  "msg.loginFailed": "Sign-in failed. Please try again.",
  "msg.emailNotVerified": "Google says this e-mail address is not verified, so we cannot sign you in with it. Verify the address in your Google account, or sign in with Meta.",
  "msg.notSignedIn": "Please sign in first",
  "msg.connectFailedProvider": "Could not connect {provider}. Please try again.",
  "msg.connectFailed": "Could not connect the source. Please try again.",
  "msg.providerConnected": "{provider} connected",
  "msg.providerAppNotConfigured": "The {provider} app is not set up by the site owner yet",
  // ADR-0065: the same words as the server (APPLE_ADS_NO_ACCOUNTS_TEXT, server/src/connections/apple-ads.ts).
  "msg.appleAdsNoAccounts":
    "This Apple Account gave V-Kit access to no Apple Ads ad account. Sign in as an Account Admin or Campaign Group Manager in Apple Ads, choose the ad account and allow access, then connect again.",

  "conn.title": "Connections",
  "conn.colSource": "Source",
  "conn.colState": "Status",
  "conn.loadFailed": "Could not load connections.",
  "conn.reauthNotice": "Re-authorize Meta to enable {sources}. The saved Meta connection was granted before these sources were added.",
  "conn.reauthNoticeGoogle":
    "Reconnect Google to add {sources}. The saved Google connection was granted before {sources} was added; GA4 and Google Ads keep working.",
  "conn.reauthBusiness": "Re-authorize to add business Pages and Instagram (read-only access to your Business portfolio).",
  "conn.reauthorizeNow": "Re-authorize",
  "conn.notConfigured": "app not configured by the owner yet",
  "conn.notConnected": "not connected",
  "conn.connected": "connected",
  "conn.until": "until {date}",
  "conn.authorize": "Authorize",
  "conn.reauthorize": "Reauthorize",
  "conn.lead":
    "Authorize the sources Claude may read for you. Everything here is read-only, except changes you confirm in chat; keys are encrypted before they are stored; your numbers are fetched when you ask and kept 24 hours at most.",
  "conn.leadPhone": "Read-only, except changes you confirm in chat. Keys encrypted. Tap a source for its status, key and instructions.",
  "conn.groupProviders": "Sign in to a provider",
  "conn.groupProvidersText":
    "One sign-in unlocks several sources. You grant the permissions in the provider's own dialog - we never see your password, and V-Kit only reads from these accounts.",
  "conn.groupKeys": "Save a key",
  "conn.groupKeysText":
    "These services have no sign-in flow. Create a key in their console and paste it here. Keys are encrypted before they are stored, never shown again, and removed the moment you ask.",
  "conn.groupNoSetup": "No setup",
  "conn.groupNoSetupText": "Public data. Works for any app - yours or a competitor's - the moment you sign in to V-Kit.",
  "conn.tag.connected": "Connected",
  "conn.tag.reauth": "Re-authorize",
  "conn.tag.unavailable": "Unavailable",
  "conn.tag.notConnected": "Not connected",
  "conn.tag.keySaved": "Key saved",
  "conn.tag.ready": "Ready",
  "conn.notSetUp": "not set up on this server yet",
  "conn.missingPermissions": "{sources} missing permissions",
  "conn.connectedAs": "Connected as {account}",
  "conn.connectedPlain": "Connected",
  "conn.authorizeWith": "Authorize with {provider}",
  "conn.authorizeAgain": "Authorize again",
  "conn.authorizeHere": "Authorize here",
  "conn.link.copy": "Copy link for another browser",
  "conn.link.explain": "Open this link where the account you need is signed in - another Chrome profile, another browser or a colleague’s computer. Choose the account, press Allow; the link works once and for 15 minutes. Then come back here: this row turns to Connected by itself.",
  "conn.link.copied": "Copied · expires in {time}",
  "conn.link.ready": "Link ready · expires in {time}",
  "conn.link.copyField": "Link for another browser",
  "conn.link.copyButton": "Copy",
  "conn.link.expired": "The link has expired. Copy a new one if you still need it.",
  "conn.link.err.limit": "You have too many open links - use or wait for one to expire.",
  "conn.link.err.generic": "Could not create the link. Try again.",
  "conn.disconnectProvider": "Disconnect {provider}",
  "conn.reauthBody":
    "This connection lacks the permissions for {missing}: they were not granted, or the connection is older than these sources. Press Authorize again and keep every box checked in {dialog} - the other sources keep working while you do.",
  "conn.unavailableHead": "Waiting on the service owner",
  "conn.unavailableText":
    "The server has no {provider} app keys yet, so there is nothing to authorize. Nothing to do on your side - the Authorize button below turns on once it's configured.",
  "conn.whatWeRead": "What we read",
  "conn.google.dialog": "Google's dialog",
  "conn.google.connected":
    "One token covers GA4, Google Ads and Search Console. Which properties and ad accounts you see is decided by this Google account's own permissions - nothing is granted here that it doesn't already have.",
  "conn.google.none":
    "Sign in with the Google account that has access to your GA4 properties, Google Ads accounts and Search Console sites. Keep all three boxes checked in Google's dialog - Google Ads has no read-only permission, but V-Kit only reads; an unchecked Search Console stays off until you authorize again.",
  "conn.google.read":
    "GA4|Reports, realtime, dimension and metric metadata\nGoogle Ads|GAQL reports; campaigns, ad groups, ads, conversion actions\nSearch Console|Search analytics, sitemaps, URL inspection",
  "conn.meta.dialog": "Facebook's dialog",
  "conn.meta.connected":
    "One token covers Meta Ads, Facebook Pages and Instagram. Which ad accounts, Pages and Instagram accounts you see is decided by this Facebook account's roles and what you picked in Facebook's dialog.",
  "conn.meta.none":
    "Sign in with Facebook and press Allow. Keep every box checked - Pages and Instagram insights need their own permissions, and the Instagram account must be a professional one linked to a Facebook Page.",
  "conn.meta.read":
    "Meta Ads|Insights; campaigns, ad sets and ads; event counts; custom conversions|ads_read\nFacebook Pages|Pages, posts, page and post insights; reactions and comment counts on posts need the optional pages_read_user_content|pages_show_list, pages_read_engagement, read_insights, pages_read_user_content\nInstagram|Profile, media, account and media insights|instagram_basic, instagram_manage_insights\nBusiness portfolio|Optional: Pages and Instagram accounts owned by your business portfolio|business_management\nEmail|Used once when connecting Meta; not stored|email",
  "conn.connectGooglePlay": "Connect Google Play",
  "conn.google_play.dialog": "Google's dialog",
  "conn.google_play.connected":
    "Which apps you see is decided by this Google account's own permissions in Play Console - nothing is granted here that it doesn't already have. V-Kit only reads: nothing is changed or posted. Paste the reports bucket below for installs, sales, earnings, subscriptions and reviews of past months.",
  "conn.google_play.none":
    "Sign in with the Google account that has access to your Play Console - it may be another account than the Google one above. Keep all three boxes checked in Google's dialog: Play has no read-only permission, but V-Kit only reads and never posts. Then paste the reports bucket below.",
  // Removed by BL-169 once Google has verified the Play permissions: this note, the key and its use above.
  "conn.google_play.unverified":
    "Until Google finishes reviewing the Play permissions, it may show “Google hasn't verified this app”: press Advanced → Go to marketing-kit.app (unsafe) → Continue.",
  "conn.google_play.read":
    "Apps|Your Play Console apps and their package names\nInstalls|Installs, uninstalls, active devices - by country, device, app version, OS version, carrier, language|View app information and download bulk reports\nSales and earnings|Estimated sales and earnings after Google's fee; totals per currency, never added up|View financial data\nSubscriptions|New, canceled and active subscription counts|View app information and download bulk reports; may also need View financial data\nReviews|The last 7 days; past months from the reports bucket. Read only, nothing is posted|may need Reply to reviews\nRefunds|Refunded and voided purchases of the last 30 days|View financial data\nAndroid vitals|Crash and ANR rates|View app information (read-only)",
  "conn.tiktok.dialog": "TikTok's dialog",
  "conn.tiktok.connected": "Which advertiser accounts you see is decided by what you allowed in TikTok's dialog.",
  "conn.tiktok.none": "Sign in to TikTok for Business and allow access to the advertiser accounts you need.",
  "conn.tiktok.read":
    "TikTok Ads|Ad spend, impressions, clicks and conversions; advertisers, campaigns, ad groups and ads. Organic TikTok isn't covered",
  // Apple Ads (ADR-0065): V-Kit is an Apple Ads service provider; the user grants access with an Apple sign-in, no keys.
  "conn.connectAppleAds": "Connect Apple Ads",
  "conn.apple_ads.dialog": "Apple's dialog",
  "conn.apple_ads.connected":
    "The label shows the organization and the ad accounts of the grant - those are the accounts V-Kit reads. Which ones they are is decided by what was chosen in Apple's dialog and by the role of the Apple Account in Apple Ads. V-Kit only reads: nothing is created, changed or deleted.",
  "conn.apple_ads.none":
    "Sign in with an Apple Account that is an Account Admin or Campaign Group Manager in Apple Ads, choose the ad account and read-only access, and allow. No keys to make.",
  "conn.apple_ads.revoke":
    "Disconnect deletes the connection in V-Kit. To withdraw V-Kit's access at Apple as well: Apple Ads → Account Settings → API → Manage API Access, the V-Kit row → Revoke Access.",
  "conn.apple_ads.read":
    "Ad accounts|The ad accounts and roles of the grant; currency, time zone, status\nCampaigns and ad groups|Status, app, daily budget, bid strategy, schedule\nKeywords and ads|Keywords and negative keywords with match type, bid and status; ads and their creative\nReports|Spend, impressions, taps, installs, CPT, CPI by campaign, ad group, keyword, search term or ad - App Store campaigns\nImpression share|Share range, rank and search popularity of an app by search term and country",
  "conn.appsflyer.dotReports": "Reports · V2 token",
  "conn.appsflyer.phone": "API token V2",
  "conn.appsflyer.intro":
    "One token reads your reports. Copying a partner integration to another app uses the same token.",
  "conn.appsflyer.read":
    "Aggregate|Installs and in-app events by day, media source, campaign and geo\nRaw|Row-level installs and events, only if your plan has Raw Data. Device IDs, IPs and customer IDs are stripped unless you explicitly ask for them.\nIntegration copy|Copies partner integration settings from one app to another; it needs your explicit yes each time",
  "conn.revenuecat.sub": "Subscriptions, MRR, churn, LTV · secret key V2",
  "conn.revenuecat.phone": "secret key V2",
  "conn.revenuecat.intro":
    "A secret API key V2 with read-only permissions. Public SDK keys (they start with appl_ or goog_) and v1 keys are rejected by RevenueCat - if you only have those, create a new one.",
  "conn.revenuecat.read":
    "Metrics|MRR, revenue, active subscriptions and trials, churn, conversion, LTV, refunds - overview and charts\nCatalog|Projects, products, entitlements, offerings\nOne customer|By app user ID - subscriptions and purchases. Email, aliases and store transaction IDs stay hidden unless you ask.",
  "conn.asc.sub": "Your own apps: sales, subscriptions, analytics, reviews · team key",
  "conn.asc.phone": "team key",
  "conn.asc.phoneSaved": "Key ID {hint}",
  "conn.appsflyer.figSummary": "Show me where - the AppsFlyer tokens card, marks 1–2",
  "conn.appsflyer.figAlt": "Where to find the AppsFlyer API token for V-Kit: AppsFlyer Security Center, the AppsFlyer tokens card and the Manage your AppsFlyer tokens link",
  "conn.appsflyer.fig1Em": "AppsFlyer tokens",
  "conn.appsflyer.fig1": " card in Security Center (profile menu, top right → Security Center).",
  "conn.appsflyer.fig2Em": "Manage your AppsFlyer tokens",
  "conn.appsflyer.fig2": " - opens the token list. Only an account admin can see and copy tokens.",
  "conn.asc.introA": "A team key from ",
  "conn.asc.introPath": "App Store Connect → Users and Access → Integrations → Team Keys",
  "conn.asc.introB": ". Apple lets you download the .p8 file once, at creation - download it then.",
  "conn.asc.figSummary": "Show me where - the Team Keys page, marks 1–4",
  "conn.asc.figAlt": "Where to create the App Store Connect API team key for V-Kit: Users and Access → Integrations → App Store Connect API → Team Keys tab",
  "conn.asc.fig1Em": "Team Keys",
  "conn.asc.fig1": " tab. Individual Keys look the same but won't work here.",
  "conn.asc.fig2Em": "Issuer ID",
  "conn.asc.fig2": " - one per team, above the table. It goes into the Issuer ID field on Connections.",
  "conn.asc.fig3Em": "Key ID",
  "conn.asc.fig3": " - the 10-character column, from the row of the key you made for V-Kit.",
  "conn.asc.fig4a": "No key yet? ",
  "conn.asc.fig4Em": "+",
  "conn.asc.fig4b": " creates one: name it, pick the role, download the .p8 - Apple shows it only once.",
  "conn.asc.openTeamKeys": "Open Team Keys in App Store Connect",
  "conn.asc.roles":
    "Role: Sales and Reports or Finance for sales, subscriptions and analytics; App Manager, Developer or Marketing for apps, reviews, versions and experiments. Admin covers everything and is the only role that can turn on Analytics Reports.",
  "conn.asc.read":
    "Sales|Downloads, redownloads, updates, in-app purchases, refunds, proceeds - by country, device, product; Pacific time, D+1\nSubscriptions|Active subscriptions per day and Apple's subscription events: subscribe, cancel, refund and more\nAnalytics|Store funnel, sources, sessions, installs, crashes. Enabling Analytics Reports is the one write - Admin key, after your yes in chat.\nApps|Your apps, reviews with your replies, versions and states, PPO experiment setup",
  "conn.apple.name": "App Store",
  "conn.apple.sub": "Public pages: version, rating, reviews, top charts by country",
  "conn.apple.phone": "public data of any app",
  "conn.apple.text":
    "No key and no sign-in: we read the same public data anyone sees in the App Store. Sales and subscriptions aren't here - for your own apps that's App Store Connect above.",
  "conn.stepByStep": "Step by step",
  "claudeLinks.connectors": "Open Connectors in Claude",
  "claudeLinks.plugins": "Open your plugins",
  "claudeLinks.hint":
    "Plugin installed: Open your plugins → v-marketing-kit → tab Connectors → Connect. Not installed: the steps on the Install page. Connector without the plugin: Open Connectors in Claude → tab Yours → v-marketing-kit (added earlier: Marketing Kit) → Connect (not listed → Add custom connector). After Connect: Google or Meta sign-in → Allow → new chat.",
  "conn.connectClaudeBanner": "Claude isn't connected yet. Install V-Kit, then come back and authorize your sources.",
  "conn.connectClaudeButton": "Install",
  "conn.newClaudeBanner": "{client} is connected. Now authorize the sources you need - Claude reads only what you connect here.",
  "conn.newClaudeHint":
    "Start with the sign-in that covers most of your sources: Google (GA4, Google Ads, Search Console) or Meta (Meta Ads, Pages, Instagram).",
  "secret.encrypted": "Encrypted",

  "secret.afToken": "API token V2",
  "secret.afTokenNote": "reports",
  "secret.afTokenPlaceholder": "Paste the V2 token",
  "secret.afTokenHint": "AppsFlyer → profile menu → Security Center → AppsFlyer tokens → API token V2. Tokens issued before 2026-03-10 no longer work.",
  "secret.rcKey": "Secret API key V2",
  "secret.rcKeyNote": "read",
  "secret.rcKeyPlaceholder": "sk_…",
  "secret.rcKeyHint":
    "Project settings → API keys → + New secret API key → version V2. Permissions: Charts & Metrics, Customer Information, Project Configuration - Read.",
  "secret.whereToFind": "Where to find it",
  "secret.googleAdsLogin": "Google Ads login customer ID",
  "secret.advanced": "Advanced · Google Ads login customer ID",
  "secret.googleAdsLoginNote":
    "Normally nothing to set: we find your manager account (MCC) ourselves. Fill this in only if an ad account is visible through several managers and you need a specific one. A value in the Claude Desktop extension settings takes priority.",
  "secret.googleAdsLoginPlaceholder": "123-456-7890",
  "secret.googleAdsLoginHint": "10 digits, top-right in Google Ads",
  "secret.storedNote":
    "Saved here, the key works in the claude.ai connector, the plugin and ChatGPT. A key entered in the Claude Desktop extension settings takes priority.",
  "secret.ascIssuer": "Issuer ID",
  "secret.ascKeyId": "Key ID",
  "secret.ascIssuerPlaceholder": "69a6de7f-…",
  "secret.ascKeyIdPlaceholder": "2X9R4HXF34",
  "secret.ascIssuerHint": "At the top of the Team Keys page, same for all keys.",
  "secret.ascKeyIdHint": "10 characters, in the keys table; also in the file name AuthKey_KeyID.p8.",
  "secret.ascPrivateKey": "Private key",
  "secret.ascPrivateKeyNote": ".p8 file",
  "secret.ascPrivateKeyEnc": "Encrypted · never shown again",
  "secret.ascDropPre": "Drop ",
  "secret.ascDropFile": "AuthKey_XXXXXXXXXX.p8",
  "secret.ascDropMid": " here or ",
  "secret.ascBrowse": "browse",
  "secret.ascDropHint": "Encrypted on arrival and never displayed again. Don't paste it into chat.",
  "secret.ascChooseFile": "Choose the .p8 file",
  "secret.ascOrPaste": "Or paste the key text",
  "secret.ascFileChosen": "File: {name}",
  "secret.ascSave": "Save key",
  "secret.ascSaved": "Key ID {hint} saved",
  "secret.ascSavedNote": "The Issuer ID and the private key are stored encrypted and never shown back.",
  "secret.ascFileUnreadable": "Could not read the file. Choose the .p8 file downloaded from App Store Connect, or paste its text.",
  "secret.ascVendor": "Vendor number",
  "secret.ascVendorNote": "for sales and subscriptions",
  "secret.ascVendorPlaceholder": "8xxxxxxx",
  "secret.ascVendorHint":
    "Payments and Financial Reports → top-left, next to your legal entity name. Apps, reviews and analytics work without it.",
  "secret.gpBucket": "Reports bucket",
  "secret.gpBucketNote": "for installs, sales, earnings, subscriptions, past reviews",
  "secret.gpBucketPlaceholder": "gs://pubsite_prod_rev_…",
  "secret.gpBucketHint":
    "Play Console → Download reports → Copy Cloud Storage URI. Nothing can find it automatically, so it is pasted here. It is a bucket name, not a secret; we keep only its ID.",
  "secret.updated": "saved {date}",
  "secret.replace": "Replace",
  "secret.remove": "Remove",
  "secret.failed": "Could not save. Please try again.",
  "secret.notConfigured": "Saving keys is not set up by the site owner yet.",

  "signin.title": "Sign in",
  "signin.text": "Sign in with Google or Meta. Your connections and your plan belong to the account you sign in with.",
  "signin.downloadTitle": "Sign in to download",
  "signin.downloadText": "Sign in to continue to your file. The download is only available to the account that created it.",
  "signin.subscribeTitle": "Sign in to subscribe",
  "signin.subscribeText": "Sign in with Google or Meta to continue to checkout. The subscription belongs to the account you sign in with.",

  "install.title": "Install",
  "install.lead": "About a minute, and nothing is downloaded to your computer. Which assistant do you use?",
  "install.pickLabel": "Assistant",
  "install.pickClaude": "Claude",
  "install.pickChatgpt": "ChatGPT",
  "install.pickMcp": "Other apps (MCP)",
  "install.mcpShort": "MCP",
  "install.pickBoth": "Use more than one? Set up each - same account, same connections.",
  "install.alsoAny": "Using another app too?",
  "install.showSteps": "Show the {name} steps",

  // Claude card: the plugin is the main path (ADR-0032), texts by the mockup checked against facts (ADR-0031).
  "install.pluginKicker": "Claude · plugin",
  "install.pluginBadge": "Skill + connector, updates itself",
  "install.pluginTitle": "Install the v-marketing-kit plugin",
  "install.pluginLead":
    "The plugin gives Claude the connector and the know-how for every source in one install. Works on claude.ai and in Claude Desktop; phones pick it up from the same account.",
  "install.plugin1a": "Open ",
  "install.plugin1Link": "Customize → Plugins",
  "install.plugin1Sub": "claude.ai/customize/plugins in a browser - the Claude Desktop app does not always open the sign-in yet",
  "install.plugin2a": "Press ",
  "install.plugin2Em": "Add → Add marketplace",
  "install.plugin2b": " and enter",
  "install.plugin3a": "Turn on ",
  "install.plugin3Em": "Sync automatically",
  "install.plugin3b": ", then search for the plugin by its name and press ",
  "install.plugin3Em2": "Add",
  "install.plugin3Sub": "Sync is how updates reach you; without it, press Check for updates now and then",
  "install.plugin4a": "Open the plugin → ",
  "install.plugin4Em": "Connectors",
  "install.plugin4Arrow": " → ",
  "install.plugin4Em2": "Connect",
  "install.plugin4b": "; sign in with Google or Meta and press Allow",
  "install.plugin4Sub": "The sign-in only identifies you - sources are authorized separately on Connections",
  "install.figWhere": "Show me where",
  "install.figLooks": "What it looks like",
  "install.fig2Alt": "Adding the V-Kit plugin marketplace in Claude: Customize → Plugins, the + Add menu with Add marketplace highlighted",
  "install.fig2Cap1Em": "+ Add",
  "install.fig2Cap1": ", top right of the Plugins page.",
  "install.fig2Cap2Em": "Add marketplace",
  "install.fig2Cap2": " - paste the address above and confirm.",
  "install.fig3Alt": "The V-Kit plugin in Claude Plugins, from marketing-kit-plugin",
  "install.fig3Cap1a": "The right one says ",
  "install.fig3Cap1Em": "from marketing-kit-plugin",
  "install.fig3Cap1b": " - press Add on it. Once installed it sits under Yours, as here.",
  "install.fig4Alt": "Connecting V-Kit in Claude: the plugin page, Connectors tab and the Connect button",
  "install.fig4Cap1a": "The ",
  "install.fig4Cap1Em": "Connectors",
  "install.fig4Cap1b": " tab inside the plugin.",
  "install.fig4Cap2Em": "Connect",
  "install.fig4Cap2": " opens the marketing-kit.app sign-in; press Allow. The line then reads Connected.",
  "install.thenTitle": "Then",
  "install.then1": "Back on this site, authorize your sources on ",
  "install.thenLink": "Connections",
  "install.then2": " and ask Claude. A good first message:",

  // The plugin comes with the Claude account: the phone app and Claude Code (synced plugins, seen 2026-11-02).
  "install.phoneNote":
    "The plugin comes with your Claude account: in the phone app and in Claude Code signed in with the same account, V-Kit is there without another install. Terminal commands are in the ",
  "install.phoneNoteLink": "Setup guide",

  // «Other apps (MCP)» (#mcp, owner 2026-11-02): any MCP app by URL; Claude Code and claude.ai without the plugin below.
  "install.mcpKicker": "Any app · MCP",
  "install.mcpBadge": "Sign-in, no keys",
  "install.mcpTitle": "Connect any MCP app",
  "install.mcpLead":
    "Cursor, VS Code, Windsurf, Claude Code and other apps that speak MCP connect to one address and sign in with Google or Meta - nothing to copy but the URL. Without the plugin's know-how, answers are a little rougher.",
  "install.mcp1": "Add a remote MCP server (Streamable HTTP) with this address:",
  "install.mcp1Sub": "Name it v-marketing-kit. Leave the OAuth client ID and secret empty - the app registers itself.",
  "install.mcp2a": "Press ",
  "install.mcp2Em": "Connect",
  "install.mcp2b": " (or Authenticate), sign in with Google or Meta and press Allow",
  "install.mcp3": " and ask. A good first message:",
  // How the OAuth sign-in goes (#mcp, owner 2026-11-02).
  "install.authTitle": "How the sign-in works",
  "install.auth1":
    "Your app calls the address and gets “sign in first” with a link to V-Kit’s OAuth settings - it finds the sign-in page by itself.",
  "install.auth2":
    "The app registers itself with V-Kit (dynamic client registration or a client metadata URL) - that is why the client ID and secret stay empty.",
  "install.auth3":
    "A browser window opens marketing-kit.app: sign in with Google or Meta, check the name of the app asking for access, press Allow.",
  "install.auth4":
    "The app receives a token for your account only (OAuth 2.1 with PKCE): it works for an hour and the app renews it by itself; after 90 days without use, sign in again. Your passwords and source keys never reach the app.",
  "install.auth5": "To cut an app off, remove it under ",
  "install.auth5Link": "Connections → Connected assistants",
  "install.authNoOauth":
    "An app that only accepts a fixed token in a header can’t connect: V-Kit gives MCP apps no static API keys, only this sign-in.",
  "install.codeTitle": "Claude Code",
  "install.codeLead": "The plugin brings the know-how along with the connector:",
  "install.code":
    "Then /reload-plugins, /mcp → v-marketing-kit → authenticate. Updates: /plugin → Marketplaces → v-marketing-kit → Enable auto-update (off by default for hand-added marketplaces).",
  "install.connectorTitle": "claude.ai without the plugin",
  "install.connector":
    "Customize → Connectors → Add custom connector. Name v-marketing-kit, leave the OAuth client fields empty. You get the tools without the know-how, so answers are rougher. On Team and Enterprise an Owner adds it for the organization first. Also the way when V-Kit doesn't show up on the phone.",

  // Fold «Had Marketing Kit before?» (#migrate, ADR-0038): the old marketing-kit items are removed once.
  "install.migrateSummary": "Had Marketing Kit before? Move to V-Kit",
  "install.migrateLead":
    "The plugin, the skill and the extension are now named v-marketing-kit. A new name is a new item: remove the old one once, otherwise Claude lists every tool twice.",
  "install.migratePluginTitle": "Plugin (claude.ai, Claude Desktop)",
  "install.migratePlugin":
    "Customize → Plugins → Marketing Kit → Uninstall. Remove the marketplace, add arey325/marketing-kit-plugin again (it is now named v-marketing-kit) and install V-Kit as in the steps above.",
  "install.migrateCodeTitle": "Claude Code",
  "install.migrateCode": "Remove the old plugin and its marketplace, then add and install V-Kit:",
  "install.migrateCmdUninstall": "claude plugin uninstall marketing-kit@marketing-kit",
  "install.migrateCmdRemove": "claude plugin marketplace remove marketing-kit",
  "install.migrateExtTitle": "Claude Desktop extension",
  "install.migrateExt": "Remove Marketing Kit in Settings → Extensions before installing V-Kit.",
  "install.migrateSkillTitle": "Skill uploaded by hand",
  "install.migrateSkill": "Delete marketing-kit in Customize → Skills.",
  "install.migrateConnectorTitle": "Custom connector",
  "install.migrateConnector":
    "A connector added by URL (https://marketing-kit.app/mcp) keeps working under the name you gave it. Don't add it a second time next to the plugin - the tools would appear twice.",

  // ChatGPT card (ADR-0066): the V-Kit plugin from the ChatGPT plugin directory.
  "install.chatgptKicker": "ChatGPT · plugin",
  "install.chatgptBadge": "Any plan",
  "install.chatgptTitle": "Use in ChatGPT",
  "install.chatgptLead": "V-Kit is in the ChatGPT plugin directory. Install it once - on any plan, Free included.",
  "install.chatgptOpen": "Open V-Kit in ChatGPT",
  "install.chatgpt1a": "Open ",
  "install.chatgpt1Link": "V-Kit in ChatGPT Plugins",
  "install.chatgpt1b": " and press ",
  "install.chatgpt1Em": "Install plugin",
  "install.chatgpt1Sub": "Or Plugins in the ChatGPT sidebar → search “V-Kit” - by VALAS OÜ",
  "install.figChatgpt1Alt": "The V-Kit page in ChatGPT Plugins with the Install plugin button",
  "install.figChatgpt1Cap": " - top right of the V-Kit page",
  "install.chatgpt2a": "Press ",
  "install.chatgpt2Em": "Connect V-Kit",
  "install.chatgpt2b": ", sign in to marketing-kit.app with Google or Meta and press ",
  "install.chatgpt2Em2": "Allow",
  "install.figChatgpt2Alt": "The ChatGPT window Connect V-Kit, developed by VALAS OÜ",
  "install.figChatgpt2Cap": " - a new tab opens marketing-kit.app",
  "install.chatgpt3a": "Press ",
  "install.chatgpt3Em": "Try in chat",
  "install.chatgpt3b": " or type @V-Kit in any chat:",
  "install.chatgpt3Sub": "Then connect your sources on ",
  // Clients (ADR-0062, BL-192): groups of connections and keys, up to three per account.
  "clients.add": "Add client",
  "clients.note": "One plugin, several clients: add a client instead of a second plugin or connector",
  "clients.stripLabel": "Clients",
  // Owner, 2026-10-15: the dialog says in one sentence how to connect an account signed in elsewhere (ADR-0063).
  "clients.modalTitle": "Add a client",
  "clients.modalText": "A client is its own group of connections and keys, one for each customer or project. You can have up to {count} in one subscription, at no extra charge. It stays one plugin for all of them: in the chat you just name the client. If the account for this client is signed in elsewhere - another browser or profile - press “Copy link for another browser” next to Authorize and open the link there.",
  "clients.nameLabel": "Name",
  "clients.namePlaceholder": "Acme",
  "clients.nameHint": "For example: Acme, My own app",
  "clients.back": "Back",
  "clients.continue": "Continue",
  "clients.adding": "Adding…",
  "clients.errName": "Enter a name of 1 to 60 characters.",
  "clients.errAdd": "Could not add the client. Try again.",
  "clients.rename": "Rename",
  "clients.renameLabel": "New name for {name}",
  "clients.save": "Save",
  "clients.errRename": "Could not rename the client. Try again.",
  "clients.delete": "Delete",
  "clients.deleteConfirm": "Delete {name} and its connections",
  "clients.deleteHint": "This disconnects its providers and removes its saved keys. It cannot be undone.",
  "clients.errDelete": "Could not delete the client. Try again.",
  "clients.errUnknown": "That client does not exist any more. Pick one from the list.",
  "clients.shown": "Showing {name}",
  "clients.preview": "Client",
  // BL-196 (owner, 2026-10-15): a warm page for everyone - we are glad, we will set it up for them, we only want to talk
  // first, and not about the price: it stays the same.
  "clientsMore.title": "Need more than {count} clients? Let’s talk.",
  "clientsMore.lead": "If you have run out of clients, you are using V-Kit exactly the way we hoped - thank you. Up to {count} clients come with every subscription; for you we will set up more.",
  "clientsMore.talk": "We only want to talk first: what you work with and what you need. Not about the price - it stays the same.",
  "clientsMore.guest": "Sign in to tell us right here:",
  "clientsMore.guestMail": "Or write to",
  "clientsMore.formTitle": "Tell us about your work",
  "clientsMore.formLead": "How many clients, which sources - a few lines are enough. The developer reads every message and answers in the chat or here.",
  "clientsMore.placeholder": "For example: ten customers, each with its own Google Ads and AppsFlyer",
  "clientsMore.sent": "Sent - thanks!",
  "clientsMore.back": "Back to Connections",

  // /sdk (owner 2026-11-02): the SDK is in preparation; the page asks, it promises no dates or prices.
  "sdk.title": "V-Kit in your own app",
  "sdk.lead": "We are preparing an SDK for products, internal agents and automations that need marketing numbers: the same thirteen sources V-Kit reads for Claude and ChatGPT, called from your own code.",
  "sdk.point1": "Your users connect their own accounts - the same sign-ins and keys as on Connections, encrypted on our side.",
  "sdk.point2": "Read-only data from ad platforms, analytics, attribution, subscriptions and the stores, in one shape.",
  "sdk.point3": "Tell us your language and stack, which sources you need and what you are building - early requests shape what comes first.",
  "sdk.now": "Need it today from an AI app? V-Kit already works as a hosted MCP server:",
  "sdk.nowLink": "any MCP app →",
  "sdk.guest": "Sign in to tell us right here:",
  "sdk.formTitle": "Request SDK access",
  "sdk.formLead": "What you are building, which sources and which language - a few lines are enough. The developer reads every message and answers here or by e-mail.",
  "sdk.placeholder": "For example: a TypeScript dashboard for our clients that reads Meta Ads, AppsFlyer and RevenueCat",
  "sdk.sent": "Sent - thanks! We will write back.",
  "meta.title.clientsMore": "More clients · V-Kit",
  "meta.title.sdk": "SDK for your app · V-Kit",
  "home.health.titleClient": "Health status · {client}",
  "install.clients": "Working for several customers? One plugin is enough: add a client for each on ",
  "install.clientsLink": "Connections",
  "guidePage.clients": "Clients",
  "guide.clients.title": "Clients: several customers, one plugin",
  "guide.clients.what": "A client is a separate group of connections and keys inside your account, one for each customer or project. Each client has its own Google, Meta, TikTok and Apple Ads sign-ins and its own AppsFlyer, RevenueCat, App Store Connect and Google Play keys, and the numbers of one client are never mixed with another’s.",
  "guide.clients.add": "To add one, open Connections, press “Add client”, type a name (for example “Acme”) and press “Continue”. The new client opens with empty source cards: connect its sources there. From the second client on, tabs above the cards switch between clients and let you rename or delete the selected one.",
  "guide.clients.elsewhereTitle": "Connecting an account that is signed in elsewhere",
  "guide.clients.elsewhere": "With more than one client, the button that connects Google, Meta, TikTok, Google Play or Apple Ads on Connections offers two ways: “Authorize here” and “Copy link for another browser”. Use the link when the account you need is signed in somewhere else - another Chrome profile, another browser or a colleague’s computer: open it there, choose the account and press Allow. The link works once and for 15 minutes. That browser does not sign in to V-Kit and does not need to; the row on Connections turns to Connected by itself.",
  "guide.clients.ask": "In the chat, just name the client: “Acme: spend last week”. With several clients and no name, V-Kit asks which one you mean instead of guessing. An account with one client is never asked.",
  "guide.clients.limit": "Up to {count} clients are included in your subscription at no extra charge. Need more? ",
  "guide.clients.moreLink": "Tell us how many and why",
  "guide.clients.agencies": "For agencies and freelancers",
  // BL-194: clients as a feature on the site. {count} - countWord(CLIENTS_DEFAULT_MAX) (web/src/lib.ts; server MK_CLIENTS_MAX, default 3).
  // Facts: ADR-0062 (up to three per account, same price, one plugin, the client named in the question; server
  // clients/resolve.ts - 409 client_required, never guessed; server/test/clients-data.test.ts).
  "clientsBanner.label": "New: clients",
  "clientsBanner.new": "New",
  "clientsBanner.text": "Clients: keep up to {count} customers’ accounts and keys in one V-Kit - one plugin, one price. Ask “Acme: spend last week”.",
  "clientsBanner.guestLink": "For agencies",
  "clientsBanner.userLink": "Add a client",
  "clientsBanner.close": "Close the announcement",
  "footer.agencies": "For agencies",
  // «…for up to three clients» in the home sources block (ADR-0062: each client has its own sign-ins and keys).
  "home.sourcesClients": "Connect them for yourself, or separately for up to {count} clients:",
  "home.sourcesClientsLink": "V-Kit for agencies and freelancers",
  "guide.clients.partner":"You may not need a client at all. If a customer gives your own account partner access (a Google Ads manager account, GA4 property sharing, or ad accounts and pages shared with your Meta Business Manager), one sign-in already sees all of their accounts, and the accounts mode of each source lists them. Use a client when a customer does not give that access, or when you work with their own keys.",
  "install.chatgptNote":
    "Everything else reads. Two tools change things - copying AppsFlyer partner-integration settings and turning on App Store Connect Analytics Reports; ChatGPT asks before they run.",


  "consent.title": "Allow access",
  "consent.expired": "This request has expired - ask your question in Claude again.",
  "consent.loadFailed": "Could not load the request.",
  "consent.sendFailed": "Could not send your answer.",
  "consent.signInFirst":
    "An app is asking for access to your V-Kit account. Sign in first - with the account you use for V-Kit.",
  "consent.ask":
    "The extension “{client}” is asking for access to your V-Kit account ({who}).",
  "consent.askHost":
    "“{client}” at {host} wants to access your V-Kit data ({who}).",
  "consent.loopbackWarning":
    "This app runs on your computer (localhost). Allow only if you started it yourself - e.g. Claude Desktop, Claude Code or MCP Inspector.",
  "consent.heading": "{client} at {host} wants to access your V-Kit data",
  "consent.returnsTo": "After you allow, the sign-in code goes to {host}.",
  "consent.headingNoHost": "{client} wants to access your V-Kit data",
  "consent.signedInAs": "Signed in as {who}",
  "consent.notYou": "not you?",
  "consent.means1": "Read the sources you authorized on Connections, when you ask it to",
  "consent.means2": "Change nothing in your accounts - the AppsFlyer and App Store Connect writes ask you first, in chat",
  "consent.means3": "Never sees your Google or Meta password, nor the keys you saved",
  "consent.nextPre": "After Allow you return to {client}. Then authorize your sources on ",
  "consent.nextLink": "marketing-kit.app/connections",
  "consent.nextPost": ".",
  "consent.revokePre": "Revoke any time on ",
  "consent.revokeLink": "Connections",
  "consent.allow": "Allow",
  "consent.deny": "Deny",

  "account.title": "Account",
  "account.lead": "Who you are to V-Kit, your plan and billing, and the one button that deletes everything tied to you. Connected assistants are on the Connections page.",
  "account.signedInCard": "Signed in with",
  "account.signedInNote.google":
    "Used only to recognise you. Access to GA4, Google Ads and Search Console is a separate grant on Connections.",
  "account.signedInNote.meta":
    "Used only to recognise you. Access to Meta Ads, Facebook Pages and Instagram is a separate grant on Connections.",
  "account.sessionCard": "Sign out and delete account",
  "account.kind.connector": "Remote connector (claude.ai, Claude Code, ChatGPT)",
  "account.kind.extension": "Claude Desktop extension",
  "account.kind.gpt": "Custom GPT (ChatGPT)",
  "account.timezone": "Time zone",
  "account.timezoneNote":
    "Periods in answers are dated in this zone unless you name another. Ad accounts and GA4 count days in their own zone; Apple and Search Console in Pacific time.",
  "account.saved": "saved",
  "account.devices": "Connected assistants",
  "account.devicesLead":
    "Each one was allowed by you on a consent screen. Revoke one and its next request is refused; your source connections and keys stay.",
  "account.noDevices": "No assistant is connected yet.",
  "account.colExtension": "Assistant",
  "account.colConnected": "Renewed",
  "account.colLastUsed": "Last request",
  "account.revoke": "Revoke",
  "account.today": "today, {time}",
  "account.devicesFailed": "Could not load the assistants.",
  "account.revokeFailed": "Could not revoke the assistant.",
  "account.timezoneFailed": "Could not save the time zone.",
  "account.logoutNote": "Sign out of this browser. Assistants keep working until you revoke them on the Connections page.",
  "account.logout": "Sign out",
  "account.deleteNote":
    "Delete the account: source connections are revoked, keys and download links are wiped, assistants are cut off. Only the request log stays - which calls were made, no longer tied to you.",
  "account.delete": "Delete account…",
  "account.deleteConfirm":
    "Delete the account? Source connections are revoked, keys and download links wiped, assistants cut off. This cannot be undone.",

  "conn.previewLink": "Data preview",

  "preview.title": "Data preview",
  "preview.intro":
    "What V-Kit sees in each connected source, for the last 7 days - a check that the right accounts are visible before you ask your assistant. Nothing here is written or changed at the source.",
  "preview.fetched": "Fetched now or from a 15-minute cache, not saved",
  "preview.permOne": "Permission",
  "preview.permMany": "Permissions",
  "preview.calls": "calls: {calls}",
  "preview.reauthorize": "Re-authorize {provider}",
  "preview.reauth.fb_pages": "Meta is connected without the Pages permissions. Authorize Meta again and keep every box checked - Ads keeps working meanwhile.",
  "preview.reauth.instagramSame": "The same re-authorization adds Instagram. The account must be professional and linked to a Facebook Page.",
  "preview.reauth.instagram": "Meta is connected without the Instagram permissions. Authorize Meta again and keep every box checked. The account must be professional and linked to a Facebook Page.",
  "preview.reauth.search_console": "Google is connected without the Search Console permission. Authorize Google again and keep every box checked - GA4 and Google Ads keep working meanwhile.",
  "preview.sources": "Sources",
  "preview.loading": "Loading…",
  "preview.err.ascKey": "No App Store Connect key is saved. Save a team API key on the Connections page.",
  "preview.err.ascVendor": "Sales need the vendor number. Save it on the Connections page.",
  "preview.none": "No rows.",
  "preview.notConnectedPre": "Connect {provider} on the ",
  "preview.notConnectedPost": " page to see its data here.",
  "preview.connectionsPage": "Connections",
  "preview.err.notAuthorized": "The source did not accept the saved access. Reconnect it on the Connections page.",
  "preview.err.notConfigured": "The source is not set up on the server. {detail}",
  "preview.err.adsTestLevel": "Google Ads API access for V-Kit is still at test level - waiting for Google approval.",
  "preview.err.adsAccess": "Google Ads refused access: {detail} (the developer token may not have the required access level yet).",
  "preview.err.source": "The source returned an error: {detail}",
  "preview.err.invalidArgs": "The request was rejected: {detail}",
  "preview.err.reauthorize": "{provider} is connected without the permissions for {source}. Re-authorize {provider} on the Connections page.",
  "preview.err.scDisabled":
    "Search Console API is not enabled in the Google Cloud project of V-Kit - the site owner has to enable it. Your Google connection is kept; GA4 and Google Ads are not affected.",
  "preview.err.serviceDisabled": "{detail}",
  "preview.err.gateway":
    "The server's answer did not get through (HTTP {status} from the network in front of the server). Try again in a minute; if it repeats, tell the site owner.",
  "preview.err.generic": "The request failed ({code}).",

  "preview.meta.title": "Meta - ad accounts",
  "preview.meta.perm": "ads_read",
  "preview.meta.calls": "accounts, objects, insights",
  "preview.meta.note": "Meta may still adjust the last 1–3 days; those numbers can change.",
  "preview.meta.colId": "id",
  "preview.meta.colName": "name",
  "preview.meta.colCurrency": "currency",
  "preview.meta.colStatus": "account_status",
  "preview.meta.noAccounts": "No ad accounts are available to this user.",
  "preview.meta.select": "Account",
  "preview.meta.campaigns": "Campaigns, last 7 days",
  "preview.meta.colCampaign": "Campaign",
  "preview.meta.colCampStatus": "Status",
  "preview.meta.colSpend": "Spend",
  "preview.meta.colImpressions": "Impressions",
  "preview.meta.colClicks": "Clicks",
  "preview.meta.colCtr": "CTR",
  "preview.meta.total": "Total",
  "preview.meta.noCampaigns": "No campaign had delivery in this period.",

  "preview.ga4.title": "Google Analytics 4",
  "preview.ga4.perm": "analytics.readonly",
  "preview.ga4.calls": "accounts, report",
  "preview.ga4.note": "GA4 finishes writing a day 24–48 hours later; today and yesterday will move.",
  "preview.ga4.colAccount": "account",
  "preview.ga4.colProperty": "property",
  "preview.ga4.colPropertyId": "property id",
  "preview.ga4.colType": "type",
  "preview.ga4.noProperties": "No GA4 properties are available to this user.",
  "preview.ga4.select": "Property",
  "preview.ga4.daily": "Users and events by day, last 7 days",
  "preview.ga4.sumEvents": "Events",
  "preview.ga4.peakDay": "Peak day",
  "preview.ga4.peakUsers": "Users that day",
  "preview.ga4.colDate": "date",
  "preview.ga4.colUsers": "active users",
  "preview.ga4.colEvents": "events",

  "preview.sc.title": "Google Search Console",
  "preview.sc.perm": "webmasters.readonly",
  "preview.sc.calls": "sites, report · last 28 days, Pacific time",
  "preview.sc.note": "Google publishes search data 2–3 days late; anonymized queries are not returned, so the queries add up to less than the totals.",
  "preview.sc.colSite": "property",
  "preview.sc.colPermission": "permission",
  "preview.sc.noSites": "No Search Console properties are available to this Google account.",
  "preview.sc.select": "Property",
  "preview.sc.daily": "Clicks and impressions by day",
  "preview.sc.colDate": "date",
  "preview.sc.colClicks": "Clicks",
  "preview.sc.colImpressions": "Impressions",
  "preview.sc.colCtr": "CTR",
  "preview.sc.colPosition": "Avg. position",
  "preview.sc.colQueryPosition": "Position",
  "preview.sc.total": "Total",
  "preview.sc.colQuery": "Top queries",

  "preview.ads.title": "Google Ads",
  "preview.ads.perm": "adwords",
  "preview.ads.calls": "accounts, report",
  "preview.ads.select": "Account",
  "preview.ads.colCustomer": "Customer ID",
  "preview.ads.colSpend": "Spend",
  "preview.ads.colClicks": "Clicks",
  "preview.ads.colImpressions": "Impressions",
  "preview.ads.colCtr": "CTR",
  "preview.ads.note": "Last 7 days; Google Ads counts days in the account's own time zone. Accounts reached through a manager account are found automatically.",
  "preview.ads.onlyManagers": "The accessible accounts are manager accounts, which have no figures of their own, and no client account is visible under them.",
  "preview.ads.noCustomers": "No Google Ads accounts are directly accessible to this user.",

  "preview.af.title": "AppsFlyer",
  "preview.af.scope": "API token V2 saved on Connections · calls: accounts, aggregate (partners report)",
  "preview.af.select": "App",
  "preview.af.noApps": "The token sees no apps.",
  "preview.af.colSource": "Media source",
  "preview.af.colClicks": "Clicks",
  "preview.af.colInstalls": "Installs",
  "preview.af.colCost": "Cost",
  "preview.af.colRevenue": "Revenue",
  "preview.af.note": "Media sources of the app for the last 7 days. Cost and revenue are in the currency AppsFlyer reports for the app; days follow your time zone when AppsFlyer has it set for the account, otherwise UTC.",
  "preview.af.noKey": "No token saved on this site. A token entered in the Claude Desktop extension stays on your computer, so this page can't use it. Save it on Connections to see your numbers here.",
  "preview.af.saveKey": "Save AppsFlyer token",
  "preview.err.afKey": "No AppsFlyer token is saved on this site. Save it on the Connections page.",
  "preview.rc.title": "RevenueCat",
  "preview.rc.scope": "Secret API key V2 saved on Connections · calls: projects, overview",
  "preview.rc.select": "Project",
  "preview.rc.noProjects": "The key sees no projects.",
  "preview.rc.mrr": "MRR",
  "preview.rc.subs": "Active subscriptions",
  "preview.rc.trials": "Active trials",
  "preview.rc.revenue": "Revenue",
  "preview.rc.newCustomers": "New customers",
  "preview.rc.note": "MRR, subscriptions and trials are the state right now; revenue and new customers cover the last 28 days, because the overview has no 7-day window. RevenueCat counts only what its SDK and store integrations saw, so its numbers differ from the stores'.",
  "preview.rc.noKey": "No key saved on this site. A key entered in the Claude Desktop extension stays on your computer, so this page can't use it. Save it on Connections to see your numbers here.",
  "preview.rc.saveKey": "Save RevenueCat key",
  "preview.err.rcKey": "No RevenueCat key is saved on this site. Save it on the Connections page.",
  "preview.play.title": "Google Play",
  "preview.play.perm": "androidpublisher, devstorage.read_only, playdeveloperreporting",
  "preview.play.calls": "apps, installs, reviews",
  "preview.play.select": "App",
  "preview.play.noApps": "No apps were found for this Google account.",
  "preview.play.installs": "Installs by day, latest 7 days with data",
  "preview.play.colInstalls": "Installs",
  "preview.play.colUninstalls": "Uninstalls",
  "preview.play.noBucket": "The reports bucket is not saved, so installs cannot be read. Save it on Connections.",
  "preview.play.saveBucket": "Save the bucket",
  "preview.play.reviews": "Reviews, last 7 days",
  "preview.play.noReviews": "No reviews with text in the last 7 days.",
  "preview.play.reviewCount": "reviews with text",
  "preview.play.avg": "average",
  "preview.play.colRating": "Rating",
  "preview.play.colReview": "Review",
  "preview.play.replied": "replied",
  "preview.play.note": "Play statistics arrive 3 to 7 days late, so the latest days are missing. The Developer API returns only reviews with text from the last 7 days.",
  "preview.reauth.google_play": "Google Play is connected without every permission it needs. Authorize Google Play again and keep every box checked.",
  "preview.err.playBucket": "The Google Play reports bucket is not saved. Save it on the Connections page.",
  "preview.apple.title": "App Store",
  "preview.apple.scope": "Public data, no sign-in · US storefront · calls: lookup, reviews",
  "preview.apple.id": "App ID",
  "preview.apple.show": "Show",
  "preview.apple.enter": "Enter an App Store app ID - the number in its store link, apps.apple.com/…/id1627714743 - to see its public page. With an App Store Connect key saved, your apps are suggested.",
  "preview.apple.version": "Version",
  "preview.apple.rating": "Rating",
  "preview.apple.ratings": "Ratings",
  "preview.apple.reviews": "Latest reviews",
  "preview.apple.noReviews": "The public feed returned no reviews.",
  "preview.apple.note": "What anyone sees on the App Store page for the US storefront: the current version, the average rating and the most recent reviews the public feed returns.",
  "preview.asc.title": "App Store Connect",
  "preview.asc.scope": "Team API key and vendor number saved on Connections · calls: apps, sales",
  "preview.asc.colId": "Apple ID",
  "preview.asc.colName": "Name",
  "preview.asc.colBundle": "Bundle ID",
  "preview.asc.noApps": "The team key sees no apps.",
  "preview.asc.sales": "Installs and proceeds by day, last 7 days",
  "preview.asc.salesNote": "Apple reports are ready about 08:00 Pacific time the next day, so the latest days may be empty.",
  "preview.asc.colDate": "Date",
  "preview.asc.colInstalls": "Installs",
  "preview.asc.colRedownloads": "Redownloads",
  "preview.asc.colProceeds": "Proceeds",
  "preview.asc.noKey": "No team key saved. Once it is, this shows your apps and, with the vendor number, the last 7 days of installs, redownloads and proceeds.",
  "preview.asc.saveKey": "Save a team key",
  "preview.asc.noVendorPre": "Sales need the vendor number: save it on the ",
  "preview.asc.noVendorPost": " page.",

  "preview.fb.title": "Facebook Pages",
  "preview.fb.perm": "pages_show_list, pages_read_engagement, read_insights",
  "preview.fb.calls": "accounts, page_insights, posts",
  "preview.fb.colId": "id",
  "preview.fb.colName": "name",
  "preview.fb.colCategory": "category",
  "preview.fb.colFollowers": "followers",
  "preview.fb.noPages": "No Facebook Pages are available to this user.",
  "preview.fb.select": "Page",
  "preview.fb.insights": "Page insights, last 7 days",
  "preview.fb.colMetric": "Metric",
  "preview.fb.colValue": "Value",
  "preview.fb.mReach": "Reach",
  "preview.fb.mViews": "Views",
  "preview.fb.mEngagements": "Engagements",
  "preview.fb.mPageViews": "Page views",
  "preview.fb.mFollows": "New follows",
  "preview.fb.posts": "Recent posts, last 7 days",
  "preview.fb.noPosts": "No posts in this period.",
  "preview.fb.colPost": "Post",
  "preview.fb.colDate": "Date",
  "preview.fb.colReactions": "Reactions",
  "preview.fb.colComments": "Comments",
  "preview.fb.colShares": "Shares",

  "preview.ig.title": "Instagram",
  "preview.ig.perm": "instagram_basic, instagram_manage_insights, pages_show_list, pages_read_engagement",
  "preview.ig.calls": "accounts, account_insights",
  "preview.ig.colId": "id",
  "preview.ig.colUsername": "username",
  "preview.ig.colName": "name",
  "preview.ig.colFollowers": "followers",
  "preview.ig.colMedia": "media",
  "preview.ig.colPage": "Facebook Page",
  "preview.ig.noAccounts": "No Instagram professional accounts linked to the user's Pages.",
  "preview.ig.select": "Account",
  "preview.ig.insights": "Account insights, last 7 days",
  "preview.ig.mReach": "Reach",
  "preview.ig.mViews": "Views",
  "preview.ig.mEngaged": "Accounts engaged",
  "preview.ig.mInteractions": "Interactions",
  "preview.ig.mFollows": "Follows and unfollows",
  "preview.ig.colMetric": "Metric",
  "preview.ig.colValue": "Value",

  "preview.tt.title": "TikTok Ads",
  "preview.tt.scope": "Reporting access · calls: accounts, report",
  "preview.tt.notConfigured": "Not set up on this server yet - nothing to do on your side.",
  "preview.tt.colId": "advertiser id",
  "preview.tt.colName": "name",
  "preview.tt.colCurrency": "currency",
  "preview.tt.colTimezone": "timezone",
  "preview.tt.colStatus": "status",
  "preview.tt.noAdvertisers": "No TikTok advertiser accounts were authorized.",
  "preview.tt.select": "Advertiser",
  "preview.tt.campaigns": "Campaigns, last 7 days",
  "preview.tt.noCampaigns": "No campaign had delivery in this period.",
  "preview.tt.colCampaign": "Campaign",
  "preview.tt.colSpend": "Spend",
  "preview.tt.colImpressions": "Impressions",
  "preview.tt.colClicks": "Clicks",
  "preview.tt.colCtr": "CTR",
  "preview.tt.colConversions": "Conversions",
  "preview.tt.total": "Total",

  "preview.aa.title": "Apple Ads",
  "preview.aa.scope": "Apple sign-in, read-only · calls: accounts, report",
  "preview.aa.select": "Ad account",
  "preview.aa.noAccounts": "The Apple Ads access covers no ad account.",
  "preview.aa.notAppStore": "This is an Apple Maps ad account; V-Kit reads App Store campaigns only.",
  "preview.aa.campaigns": "Campaigns, last 7 days",
  "preview.aa.noCampaigns": "No campaign had delivery in this period.",
  "preview.aa.colCampaign": "Campaign",
  "preview.aa.colSpend": "Spend",
  "preview.aa.colImpressions": "Impressions",
  "preview.aa.colTaps": "Taps",
  "preview.aa.colTtr": "TTR",
  "preview.aa.colInstalls": "Installs",
  "preview.aa.total": "Total",
  "preview.aa.note": "Last 7 days in the ad account's time zone. Installs are counted by Apple Ads attribution (tap-through and view-through); spend is in the ad account's currency. Recent days may still change.",

  "support.h1": "Support",
  "support.lead": "Something does not work, or you have a question about V-Kit? Write to the developer or find the answer in the guide.",
  "support.contactTitle": "Write to us",
  "support.contactText": "Email the developer at",
  "support.readsAll": "The developer reads every message.",
  "support.helpTitle": "Help on the site",
  "support.guideLink": "Setup guide: install V-Kit and connect every source",
  "support.installLink": "Install: the plugin for Claude and ChatGPT",
  "support.legalTitle": "Policies",
  "support.privacyLink": "Privacy",
  "support.termsLink": "Terms",
  "support.feedbackTitle": "Feedback from your account",
  "support.signedIn": "Signed in? Send a note to the developer below.",
  "guidePage.h1": "Setup guide",
  "guidePage.lead": "Install V-Kit in Claude or ChatGPT, then connect your sources. Every step is on this page, one section per source.",
  "guidePage.toc": "On this page",
  "guidePage.install": "Install",
  "guidePage.sources": "Connect your sources",
  "guidePage.connect": "Connect on the Connections page",
  "guide.needsTitle": "What you need",
  "guide.stepsTitle": "How to connect",
  "guide.errorsTitle": "Common errors and what to do",
  "guide.commonTitle": "Errors you may see with any source",
  "guide.close": "Close",
  "guide.open": "Instructions: {source}",
  "guide.authorize": "Authorize with {provider}",
  "guide.reconnect": "Reconnect {provider}",
  "guide.signIn": "Sign in with {provider} to connect",
  "guide.openInstall": "How to connect to Claude",
  "guide.openConnections": "Open Connections",
  "guide.installStepsTitle": "How to install",
  "guide.directLink": "Direct link to this instruction: {url}",
  "guide.more": "More about {source} →",

  "guide.plugin.title": "V-Kit plugin",
  "guide.plugin.intro":
    "The main path in Claude: one install gives Claude the connector and the know-how for every source, and it updates itself. Start at claude.ai/customize/plugins in a browser - full steps with screenshots on marketing-kit.app/install. Until it is in the Claude directory, you add it from its GitHub repository: arey325/marketing-kit-plugin. Without the plugin: Customize → Connectors → Add custom connector → https://marketing-kit.app/mcp.",
  "guide.plugin.needs":
    "A Claude account on a plan with Customize → Plugins (on Team and Enterprise an Owner decides whether members can add their own marketplace).\nA V-Kit account: you sign in with Google or Meta when you connect.",
  "guide.plugin.steps": [
    "On claude.ai in a browser open claude.ai/customize/plugins (Customize → Plugins; in Claude Desktop - Customize in the sidebar) → Add → “Add marketplace” and enter arey325/marketing-kit-plugin.",
    "Open the marketplace and turn on “Sync automatically”; claude.ai shows Discover - switch to Yours or type “marketing” in the search box and pick v-marketing-kit; then press Add / Install. (No marketplace option: download the repository zip from github.com/arey325/marketing-kit-plugin and use Add → “Upload plugin”.)",
    "Claude Code: run claude plugin marketplace add arey325/marketing-kit-plugin and claude plugin install v-marketing-kit@v-marketing-kit (inside a session: /plugin marketplace add …, /plugin install …), then /reload-plugins and /mcp → v-marketing-kit → authenticate.",
    "Phone: plugins are added on the web or in Claude Desktop; once installed they are available in chats in the mobile apps with the same account. If not, use the connector: claude.ai/customize/connectors → tab Yours → v-marketing-kit → Connect (or Add custom connector → https://marketing-kit.app/mcp).",
    "After installing: open claude.ai/customize/plugins → Yours → v-marketing-kit → tab Connectors → Connect, sign in with Google or Meta → Allow → new chat.",
    "Had Marketing Kit before? Remove the old Marketing Kit plugin, the marketing-kit skill you uploaded by hand (Customize → Skills) and the Claude Desktop extension, so there are not two sets of tools - steps on marketing-kit.app/install#migrate.",
  ].join("\n"),
  "guide.plugin.errors":
    "No “Add marketplace” button → on Team and Enterprise an Owner controls this: ask the Owner to allow it or to add arey325/marketing-kit-plugin for the organization.\nThe Connectors tab says “Not connected” → press Connect and sign in; the plugin's skill loads either way, but the tools work only when the connector is connected.\nV-Kit tools are listed twice → the old Marketing Kit plugin, the skill zip, the extension or a custom connector is installed as well; keep one of them (marketing-kit.app/install#migrate).\nClaude Code: plugin not found → run claude plugin marketplace list; the marketplace is named v-marketing-kit.\nNothing changed after an update → press “Check for updates” on the marketplace (Claude Code: /reload-plugins).",

  "guide.chatgpt.title": "V-Kit in ChatGPT",
  "guide.chatgpt.intro":
    "V-Kit is in the ChatGPT plugin directory: https://chatgpt.com/plugins/plugin_asdk_app_6acc18d0fe108191b079513f43b725c9. It works on any ChatGPT plan, Free included.",
  "guide.chatgpt.needs": [
    "A ChatGPT account, any plan.",
    "A V-Kit account: you sign in with Google or Meta when ChatGPT connects.",
  ].join("\n"),
  "guide.chatgpt.steps": [
    "Open V-Kit in ChatGPT Plugins (the link above, or Plugins in the ChatGPT sidebar → search “V-Kit” - by VALAS OÜ) and press Install plugin.",
    "Press Connect V-Kit: a new tab opens marketing-kit.app - sign in with Google or Meta and press Allow.",
    "Press Try in chat, or type @V-Kit in any chat, and ask “Check my V-Kit connections”.",
    "Connect your sources and save the AppsFlyer and RevenueCat keys on the Connections page - ChatGPT uses what is saved there.",
  ].join("\n"),
  "guide.chatgpt.errors":
    "V-Kit is not found in Plugins → open the direct link above; in a Business or Enterprise workspace an admin may have to allow the plugin.\nThe tab with marketing-kit.app did not open → allow pop-ups for chatgpt.com and press Connect V-Kit again.\n“source_not_authorized” in an answer → connect that source on https://marketing-kit.app/connections.\nV-Kit tools appear twice → remove an old V-Kit app you added in Developer mode (Plugins → Installed).",

  "guide.ga4.title": "Google Analytics 4",
  "guide.ga4.intro": "Reports, realtime and metadata of your GA4 properties - read-only (analytics.readonly).",
  "guide.ga4.needs":
    "A Google account with at least Viewer access to the GA4 property (GA4 → Admin → Property access management).\nAn account on this site (sign in with Google or Meta).",
  "guide.ga4.steps":
    "On this page press “Authorize with Google” - one consent covers GA4, Google Ads and Search Console.\nPick the Google account that sees your GA4 property and leave every checkbox ticked.\nBack here the Google row shows “Connected”. Open Data preview to check: the GA4 section lists your properties.\nIn Claude, ask about users, sessions or events - the property id comes from the list of properties.",
  "guide.ga4.errors":
    "No GA4 properties are listed → the Google account you authorized has no access to the property; ask for Viewer access or reconnect with the right account.\n“Google Analytics Data API is not enabled in the Google Cloud project …” → a setting of the site owner, not yours; the message has the enable link for the owner. Your connection is kept.",

  "guide.google_ads.title": "Google Ads",
  "guide.google_ads.intro": "Campaign, ad group and ad reports via GAQL - read-only (adwords).",
  "guide.google_ads.needs":
    "A Google account with access to the Google Ads account (any access level).",
  "guide.google_ads.steps":
    "Press “Authorize with Google” (the same consent as GA4 and Search Console).\nNothing else to enter: if the account is reached through a manager account (MCC), V-Kit finds the manager account itself. Only when an ad account is reachable through several manager accounts and you need a specific one: open Google on this page → “Advanced · Google Ads login customer ID” (10 digits, hyphens allowed); a value in the Claude Desktop extension settings takes priority over it.\nAsk Claude about spend, clicks or conversions; the list of accounts - “which Google Ads accounts do I have” (direct and through manager accounts, with name and status).",
  "guide.google_ads.errors":
    "“Google Ads API access for V-Kit is still at test level” → the server's developer token is waiting for Google's approval (Basic access). At test level Google Ads answers only for test accounts - nothing to fix on your side, it starts working once Google approves.\nUSER_PERMISSION_DENIED or “customer not found” → the account is not visible to the connected Google account (neither directly nor through a manager account V-Kit can reach): check your access to it in Google Ads → Admin → Access and security. The Google connection stays as it is.\n“The Google Ads developer token is not set up by the owner yet” → the site owner has not configured Google Ads yet.",

  "guide.search_console.title": "Google Search Console",
  "guide.search_console.intro": "Organic Google Search: clicks, impressions, CTR and position by query, page, country, device; sitemaps; URL index status - read-only (webmasters.readonly).",
  "guide.search_console.needs":
    "A Google account added to the property in Search Console (Settings → Users and permissions).",
  "guide.search_console.steps":
    "Press “Authorize with Google”. If the Google row says “Search Console missing permissions”, press “Authorize again” and keep the Search Console checkbox.\nOpen Data preview: the Search Console section lists your properties and the last 28 days.\nIn Claude, ask about search queries, pages or positions. The property looks like https://example.com/ or sc-domain:example.com.",
  "guide.search_console.errors":
    "“Reconnect Google to add Search Console” → re-authorize Google and keep the Search Console checkbox; GA4 and Google Ads keep working meanwhile.\n“Search Console API is not enabled in the Google Cloud project …” → a setting of the site owner; your connection is kept.\nThe last 2–3 days are empty → Google publishes search data with a 2–3 day delay; not an error.",

  "guide.meta.title": "Meta Ads",
  "guide.meta.intro": "Facebook and Instagram ad performance, campaigns, ad sets and ads - read-only (ads_read).",
  "guide.meta.needs":
    "A Facebook account with access to the ad account (Business Manager → Ad accounts, any role).",
  "guide.meta.steps":
    "Press “Authorize with Meta” on this page; in the Facebook dialog keep all permissions and pick the businesses and pages.\nOne Meta connection serves Meta Ads, Facebook Pages and Instagram.\nOpen Data preview: the Meta section lists your ad accounts and last-7-day campaigns.",
  "guide.meta.errors":
    "No ad accounts are listed → the Facebook account has no role in the ad account; ask the admin to add you in Business settings.\nThe connection expired (about 60 days) → press “Authorize again”.",

  "guide.fb_pages.title": "Facebook Pages",
  "guide.fb_pages.intro": "Pages, posts, page and post insights - read-only (pages_show_list, pages_read_engagement, read_insights).",
  "guide.fb_pages.needs":
    "A Facebook account with a role on the Page (or a task in the Business portfolio that owns it).",
  "guide.fb_pages.steps":
    "Press “Authorize with Meta” - or “Authorize again” if the Meta row says “Facebook Pages … missing permissions”. Re-authorizing adds the Page permissions; the Ads connection keeps working.\nIn the Facebook dialog choose the Pages to share and keep all checkboxes.\nOpen Data preview: the Facebook Pages section shows your Pages, 7-day insights and recent posts.",
  "guide.fb_pages.errors":
    "“Re-authorize Meta to enable Facebook Pages” → the saved Meta connection was granted before Pages were added; re-authorize and keep all checkboxes.\nA Page is missing → it was not selected in the Facebook dialog; re-authorize and select it.",

  "guide.instagram.title": "Instagram",
  "guide.instagram.intro": "Instagram professional account profile, insights, media and media insights - read-only.",
  "guide.instagram.needs":
    "An Instagram Business or Creator account (not a personal one): Instagram → Settings → Account type and tools → Switch to professional account.\nThe Instagram account linked to a Facebook Page you manage: Page settings → Linked accounts → Instagram.",
  "guide.instagram.steps":
    "Press “Authorize with Meta” - or “Authorize again” if the Meta row says “… Instagram missing permissions”. This adds the Instagram permissions; the Ads connection keeps working.\nIn the Facebook dialog select the Page the Instagram account is linked to, and the Instagram account itself.\nOpen Data preview: the Instagram section shows the account and its 7-day insights.",
  "guide.instagram.errors":
    "“No Instagram professional accounts linked to the user's Pages” → switch the account to Business/Creator and link it to a Page, then re-authorize Meta.\n“Re-authorize Meta to enable Instagram” → re-authorize and keep all checkboxes; Meta adds permissions only through a new consent.",

  "guide.tiktok.title": "TikTok Ads",
  "guide.tiktok.intro": "TikTok advertiser accounts, ad performance reports, campaigns, ad groups and ads - read-only. Organic TikTok (videos, followers) is not covered.",
  "guide.tiktok.needs":
    "A TikTok for Business account with access to the advertiser account (TikTok Ads Manager).",
  "guide.tiktok.steps":
    "Press “Authorize with TikTok” in the TikTok row; sign in to TikTok for Business and allow access for the advertiser accounts you need.\nOpen Data preview: the TikTok Ads section lists your advertisers and 7-day campaigns.",
  "guide.tiktok.errors":
    "TikTok row says “not configured by the owner” or “Unavailable” → the TikTok app is not set up on this server; this should not happen now. Tell the developer through the feedback form at the bottom of this page.\nTikTok shows a redirect error → write to the developer with the exact text; the redirect address of the app must match this site.\nAn advertiser is missing → it was not selected when authorizing; press “Authorize again” and select it.",

  "guide.apple_ads.title": "Apple Ads",
  "guide.apple_ads.intro":
    "Apple Ads - the App Store search ads - of your ad accounts: campaigns, ad groups, keywords, negative keywords, ads, performance reports by campaign, ad group, keyword, search term or ad, and impression share. Read-only: nothing is ever created, changed or deleted. Apple Maps ad accounts are not covered.",
  "guide.apple_ads.needs":
    "An Apple Account that is an Account Admin or Campaign Group Manager in Apple Ads - only these roles can give a service provider access.\nNo key, no API user and no certificate: V-Kit is connected with Apple's own sign-in.",
  "guide.apple_ads.steps":
    "On the Connections page press “Connect Apple Ads”.\nSign in with the Apple Account that is an Account Admin or Campaign Group Manager in Apple Ads.\nIn Apple's dialog choose the ad account and read-only access - V-Kit only reads - and allow.\nBack on Connections the Apple Ads row reads “Connected as” the organization and its ad accounts: those are the ad accounts V-Kit sees.\nIn Apple Ads, Account Settings → API → Manage API Access now lists V-Kit as a service provider, with who granted the access, the account and the role. Revoke Access there withdraws it at any time.\nOpen Data preview: the Apple Ads section lists your ad accounts and the campaigns of the last 7 days. Or ask Claude “Apple Ads spend last week by campaign”.",
  "guide.apple_ads.errors":
    "“apple_ads_no_accounts” or “gave V-Kit access to no Apple Ads ad account” → the Apple Account is not an Account Admin or Campaign Group Manager in Apple Ads, or no ad account was chosen; sign in with such an account, choose the ad account and connect again.\nThe Apple Ads row says “Unavailable” or “not set up on this server yet” → the Apple Ads connection is not set up by the owner yet; nothing to do on your side - the button turns on once it is.\n“source not authorized” after it worked → the access was revoked in Apple Ads or has expired; press “Connect Apple Ads” again.\nAn error naming the roles of the grant (403) → the grant has no role for that ad account or request; give access again in Apple Ads and choose that account.\nToo many requests (429) → Apple's request limit; ask again in a minute.\nAn ad account is missing → it was not part of the grant; press “Connect Apple Ads” again and choose it.\nAfter Disconnect, V-Kit is still listed in Apple Ads → Disconnect deletes only the connection in V-Kit; revoke it in Account Settings → API → Manage API Access → Revoke Access.",

  "guide.appsflyer.title": "AppsFlyer",
  "guide.appsflyer.intro": "Installs and in-app events by media source, campaign and geo; raw data. Saved encrypted on the server, or kept in the Claude Desktop extension.",
  "guide.appsflyer.needs":
    "An AppsFlyer user with access to the apps (an admin can issue tokens for the account).\nOptional: the V-Kit extension in Claude Desktop, if you prefer to keep the token there.",
  "guide.appsflyer.steps":
    "In AppsFlyer open the profile menu (your e-mail, top right) → Security Center. On the “AppsFlyer tokens” card (mark 1 on the screenshot) press “Manage your AppsFlyer tokens” (mark 2). Only an account admin sees the tokens.\nCopy the API token V2 - it reads reports through the Pull API (aggregate and raw data). Tokens issued before 2026-03-10 are revoked: create a new one if yours is older.\nOn this page (Connections), under AppsFlyer, paste it into “API token V2” and press Save. It is stored encrypted on marketing-kit.app. Alternatively, in the Claude Desktop extension: Settings → Extensions → v-marketing-kit → “AppsFlyer token”; a token entered there takes priority.\nAsk Claude about installs by media source.",
  "guide.appsflyer.errors":
    "“appsflyer_token_missing” → the token is not saved here and not in the extension; paste it on this page (step 3).\n401 / “token is not valid” → the token was revoked or issued before 2026-03-10; create a new API token V2.\n“no Raw Data module” → raw reports need the Raw Data add-on in your AppsFlyer plan; aggregate reports work without it.",

  "guide.revenuecat.title": "RevenueCat",
  "guide.revenuecat.intro": "Subscription metrics (MRR, active subscriptions and trials, revenue), charts, products, entitlements, offerings and one customer's subscriptions - read-only, REST API v2. Saved encrypted on the server, or kept in the Claude Desktop extension.",
  "guide.revenuecat.needs":
    "A RevenueCat collaborator role that can manage API keys in the project.\nOptional: the V-Kit extension in Claude Desktop, if you prefer to keep the key there.",
  "guide.revenuecat.steps":
    "In RevenueCat open your project → Project settings → API keys → “+ New secret API key”.\nName it (for example “V-Kit”), choose version V2 and set permissions: Charts & Metrics - Read, Customer Information - Read, Project Configuration - Read; everything else - No access. Press Generate and copy the key (sk_…); it is shown once.\nOn this page (Connections), under RevenueCat, paste it into “Secret API key V2” and press Save. It is stored encrypted on marketing-kit.app. Alternatively, in the Claude Desktop extension: Settings → Extensions → v-marketing-kit → “RevenueCat secret API key”; a key entered there takes priority.\nAsk Claude “what is our MRR” or “trial conversion last month”.",
  "guide.revenuecat.errors":
    "“revenuecat_key_missing” → the key is not saved here and not in the extension; paste it on this page (step 3).\n401 authentication_error → the key is a v1 key or a public SDK key (appl_, goog_); issue a secret key with version V2.\n403 authorization_error → the key lacks a Read permission for that area or belongs to another project; edit its permissions or issue a new key.\nRate limit → charts and metrics allow 25 requests per minute per key; wait a minute.",

  "guide.app_store_connect.title": "App Store Connect",
  "guide.app_store_connect.intro":
    "Your own apps in App Store Connect: apps, sales and downloads by country and device, subscription reports and events, App Store funnel, sources, sessions and crashes, reviews, versions and Product Page Optimization setup. Team API key, read-only except one confirmed action. This is not the public App Store data (that is the “Apple App Store” source).",
  "guide.app_store_connect.needs":
    "An Apple Developer account with App Store Connect access. Only the Account Holder or an Admin can create team keys; the first time, the Account Holder must press “Request Access” in the API section.\nA team key, not an individual key: individual keys cannot read Sales and Trends. A team key sees all apps of the team.\nFor sales and subscription reports: the vendor number of the team.",
  "guide.app_store_connect.steps":
    "Open https://appstoreconnect.apple.com/access/integrations/api → tab Team Keys (not Individual Keys). Only the Account Holder and Admins see it; if there is a “Request Access” button, the Account Holder presses it once.\nIssuer ID - above the key table, with a Copy button (one per team).\nPress “+”, name the key “V-Kit”, Access: Admin (the only role that covers every mode).\nIn the new key's row: Key ID (10 characters) and Download for the .p8 file - Apple lets you download it only once.\nVendor number - App Store Connect → Payments and Financial Reports, top left under your company name.\nEnter all of it here under App Store Connect and press Save key (the vendor number has its own Save). It is stored encrypted and never shown back. Then ask Claude “installs and proceeds last week”.",
  "guide.app_store_connect.errors":
    "“app_store_connect_key_missing” → no key is saved; enter it on this page (step 6).\n“app_store_connect_vendor_number_missing” → sales and subscriptions need the vendor number; save it on this page (step 5).\n401 / “key is not valid” → the key was revoked or the Issuer ID, Key ID and .p8 do not belong together; create a new team key and save all three again.\n403 → the key's role is too narrow for that mode (for example, enabling Analytics Reports needs Admin); create a key with a wider role.\nNo sales for a date → Apple has no report yet (reports appear about 08:00 Pacific time the next day) or the vendor number is wrong.",

  "guide.google_play.title": "Google Play",
  "guide.google_play.intro":
    "Your own Android apps in Play Console: installs and uninstalls, estimated sales and earnings, subscription counts, reviews, refunds, and crash and ANR rates. Read-only: nothing is ever changed or posted. This is not public Google Play data about other apps.",
  "guide.google_play.needs":
    "A Google account with access to your Play Console (the account owner adds it in Users and permissions). It may be another account than the one you use for GA4 and Google Ads.\nThe Play Console permission “View app information and download bulk reports (read-only)”, set to Global: installs, subscription counts, reviews of past months and Android vitals.\nThe permission “View financial data”, set to Global: sales, earnings and refunds. Subscription counts may need it too - Google does not say which of the two opens them.\nThe reports bucket address from Play Console → Download reports → Copy Cloud Storage URI. Nothing can find it automatically.",
  "guide.google_play.steps":
    "On the Connections page press “Connect Google Play”.\nPick the Google account that has access to your Play Console.\nKeep all three permissions checked in Google's dialog and press Allow. One reads “View and manage your Google Play Developer account”: Google has no read-only version of it, but V-Kit only reads.\nIn Play Console open Download reports, press “Copy Cloud Storage URI”, paste it into “Reports bucket” on the Connections page and press Save. It is not a secret; V-Kit keeps only the bucket ID.\nAsk Claude “installs of my Android app last month” or “reviews of the last 7 days”.",
  "guide.google_play.errors":
    "“google_play_bucket_missing” → paste the reports bucket on the Connections page (step 4). Installs, sales, earnings, subscriptions and reviews of a past month need it; apps, recent reviews, refunds and vitals work without it.\n403 naming a Play Console permission → this Google account lacks it; the Play Console account owner grants it in Users and permissions.\nRecent reviews do not come back → ask the account owner to grant “Reply to reviews” as well. V-Kit only reads reviews and never posts a reply.\n404 on the bucket → copy the Cloud Storage URI again from Download reports.\nThe last 3–7 days are empty → Google posts Play reports 3–7 days late, and earnings around the 5th of the next month; not an error.\nOnly the last 7 days of reviews → that is all Google's API returns; ask for a past month to read the monthly reviews report. Refunds cover the last 30 days.\nNo app listed → the Google account you picked has no access to it; connect Google Play again and pick another account.",

  "guide.apple.title": "Apple App Store",
  "guide.apple.intro": "Public App Store data (not your App Store Connect account): app details, ratings, reviews and top charts. No authorization, no key.",
  "guide.apple.needs": "Nothing - the data is public.\nThe App Store ID of the app (the number after “id” in the App Store link) or its bundle ID.",
  "guide.apple.steps":
    "Connect V-Kit to Claude (Install page, “Connect to Claude”) and ask, for example: “rating and latest reviews of app id1627714743 in the US”.\nRatings and reviews are per App Store country - name the country.",
  "guide.apple.errors": "“No results” → check the App Store ID and the country: an app may not be available in every store.",

  "guide.common.errors":
    "“source not authorized” → the source is not connected (or the provider revoked access): press Authorize on the Connections page.\n“Reconnect to add …” / “Re-authorize … to enable …” → the saved connection was granted before this source was added; authorize the provider again and keep every checkbox. Other sources keep working.\n“not configured by the owner” → the site owner has not set up this provider yet; nothing to do on your side.\nGoogle “Google hasn't verified this app” → while Google's review is pending, press Advanced → “Go to marketing-kit.app (unsafe)” → Continue.\nMeta “App not active” / “This app isn't available” → the Meta app is in development mode or waiting for Meta's review; only people with a role in the app can connect for now - ask the site owner.",

  "footer.trust": "Read-only by design · keys encrypted at rest · your data is fetched, not stored",
  "footer.copy": "© 2026 {name} · {city}, {country}",
  "footer.refunds": "Refunds",
  "footer.about": "Marketing Kit: your ad, app and subscription numbers - in Claude and ChatGPT.",
  "footer.guide": "Setup guide",
  "footer.support": "Support",
  "footer.vs": "vs Others",
  "footer.anyApp": "Any MCP app",
  "footer.privacy": "Privacy",
  "footer.terms": "Terms",
  "footer.deletion": "Data deletion",
  "footer.sources": "Sources",
  "footer.src.asc": "App Store Connect",
  "footer.src.appsflyer": "AppsFlyer",
  "footer.src.revenuecat": "RevenueCat",
  "footer.src.mobile": "Mobile app marketing",
  "footer.src.ga4": "GA4",
  "footer.src.searchConsole": "Search Console",
  "footer.src.googleAds": "Google Ads",
  "footer.src.metaAds": "Meta Ads",
  "footer.src.tiktok": "TikTok Ads",
  "footer.src.appleAds": "Apple Ads",
  "footer.src.googlePlay": "Google Play",
  "footer.src.instagram": "Instagram",
  "footer.src.fbPages": "Facebook Pages",

  // /pricing - the mockup «Pricing» (ADR-0031), every sentence checked against ADR-0045/0046/0047.
  "pricing.metaTitle": "Pricing · V-Kit · Marketing Kit",
  "pricing.metaDescription": "One price, everything included. €19.99 a month or €199 a year, VAT included where it applies. 30 free days, no card.",
  "pricing.kicker": "Pricing",
  "pricing.h1": "One price. Everything.",
  "pricing.lead": "All thirteen sources, every assistant, every question. No plan that unlocks “3 connectors”, no seat math, no “contact sales”. The price you see is the price you pay - VAT included where it applies.",
  "pricing.incl.sources": "Meta Ads, Facebook Pages, Instagram, TikTok Ads, Apple Ads, Google Ads, GA4, Search Console, AppsFlyer, RevenueCat, App Store Connect, Google Play, App Store",
  "pricing.incl.assistants": "Claude, ChatGPT and Claude Code - switch or use all three",
  "pricing.incl.accounts": "Unlimited ad accounts, properties and apps under your sign-in - every question, as often as you like",
  // ADR-0062 (BL-194): up to MK_CLIENTS_MAX (3) clients, each its own connections and keys, no per-client charge.
  // The «New» tag next to it (pricing.new): New until 2026-11-15 - then CLIENTS_NEW = false in ../lib.ts
  // (one month after clients went live; /pricing and /subscribe).
  "pricing.incl.clients": "Up to {count} clients - separate connections and keys for each customer",
  // Owner, 2026-10-15: a link after the clients line - «if I need more» → the page that explains (/clients/more).
  "pricing.incl.clientsMore": "if I need more",
  "pricing.new": "New",
  "pricing.incl.privacy":"Read-only, keys encrypted, nothing stored - included because it's how it's built, not a tier",
  "pricing.plan": "The plan",
  "pricing.onlyPlan": "The only plan",
  "pricing.period": "Billing period",
  "pricing.monthly": "Monthly",
  "pricing.yearly": "Yearly",
  "pricing.priceMonthly": "€19.99",
  "pricing.priceYearly": "€199",
  "pricing.perMonth": "/ month",
  "pricing.perYear": "/ year",
  "pricing.noteMonthly": "Or €199 a year - save €40 (17%). VAT included where it applies.",
  "pricing.noteYearly": "That is €16.58 a month - €40 (17%) less than paying monthly. VAT included where it applies.",
  "pricing.start": "Start 30 free days",
  "pricing.startHint": "No card to start. Nothing is charged until you decide.",
  "pricing.subscribe": "Subscribe",
  "pricing.trialHint": "Your trial has {days} days left. Subscribe now and keep them.",
  "pricing.giftHint": "Your gift subscription is running. To keep access after it ends, subscribe - you can do it now and pay nothing until the gift ends.",
  "pricing.expiredHint": "Your trial has ended. Nothing was deleted - subscribe and ask again.",
  "pricing.manage": "Manage your plan",
  "pricing.subscribedHint": "You're subscribed. Plan, card and invoices are in Account.",
  "pricing.compLink": "Go to Account",
  "pricing.compHint": "Your access is complimentary - there is nothing to pay.",
  "pricing.openSoon": "Subscriptions open soon.",
  "pricing.vat": "VAT",
  "pricing.vatText": "Included in the price where it applies. Stripe sells the subscription as merchant of record: it calculates and collects the tax and sends the receipt and the invoice.",
  "pricing.cancel": "Cancel",
  "pricing.cancelText": "One click in Account, any time. You keep access to the end of what you paid for.",
  "pricing.refund": "Refund",
  "pricing.refundText": "Within 14 days after any charge, in full.",
  "pricing.refundLink": "How refunds work",
  "pricing.notHere": "What you won't find here",
  "pricing.notHereLead": "The usual pricing page is a table that makes you guess which column you are. Ours has one column, so here is the list of things we chose not to charge for separately.",
  "pricing.nh1.title": "Starter · Pro · Business",
  "pricing.nh1.body": "There is one plan and it is all of it. Nothing is held back for a higher tier.",
  "pricing.nh2.title": "“Up to 3 connectors”",
  "pricing.nh2.body": "Thirteen sources, all included. Connect two or all of them - same price.",
  "pricing.nh3.title": "Per seat",
  "pricing.nh3.body": "The plan is yours. One sign-in, your accounts, whatever assistant you open it in.",
  "pricing.nh4.title": "Query limits",
  "pricing.nh4.body": "Ask as much as you work. The only limits are the ones Meta, Google and the rest put on their APIs.",
  "pricing.nh5.title": "“Contact sales”",
  "pricing.nh5.body": "No quotes, no calls. Larger teams: each person subscribes.",
  "pricing.how": "How the 30 days work",
  "pricing.howLead": "Sign in, connect what you have, ask away. We remind you in the chat and on this site - 10 days before the end and the day before. If you do nothing, the assistant simply stops answering - no surprise charge, nothing deleted.",
  "pricing.day1": "Day 1",
  "pricing.day1Text": "Sign in with Google or Meta. The trial starts now - 30 days, no card, every source open.",
  "pricing.day21": "Day 21",
  "pricing.day21Text": "Ten days left: the assistant says so in the chat and the Home page shows where to subscribe. Subscribe early and you keep the remaining days - the first charge is on day 31.",
  "pricing.day29": "Day 29",
  "pricing.day29Text": "The reminder again, the day before the last day. Connections and Account keep working regardless.",
  "pricing.day31": "Day 31",
  "pricing.day31Text": "Not subscribed: the assistant answers “subscription needed” with a link and offers 3 more days, once. Nothing else changes - your keys and connections stay until you delete them.",
  "pricing.faq": "Questions people ask before paying",
  "pricing.faq1.q": "Why one price when everyone else has three?",
  "pricing.faq1.a": "Because the data you connect doesn't cost us three different amounts. Tiered pricing exists to make you pay for the column you're afraid of outgrowing. We'd rather you pay €19.99 and never think about it again.",
  "pricing.faq2.q": "Is €19.99 with or without VAT?",
  "pricing.faq2.a": "With. Stripe sells the subscription as merchant of record: at checkout it works out the tax for your country, and where VAT applies it is already in the price. Stripe may show the price in your local currency before you pay; the receipt and the invoice show the tax.",
  "pricing.faq3.q": "Who sends the invoice?",
  "pricing.faq3.a": "Stripe, as the seller. It issues the invoice and the receipt for every charge to the details you give on its checkout page and e-mails them to you; each invoice can also be downloaded as PDF from Account.",
  "pricing.faq4.q": "What happens if I stop paying?",
  "pricing.faq4.a": "If a charge fails, Stripe retries for up to a week and emails you; access continues meanwhile. After that the assistant returns “subscription needed”. Cancelling yourself: access until the end of the paid period, then the same. Your keys and connections stay either way.",
  "pricing.faq5.q": "Monthly now, yearly later?",
  "pricing.faq5.a": "Switch any time in Account. Going yearly is prorated - you pay the difference for the rest of the current month. Going back to monthly takes effect when the year ends.",
  "pricing.faq6.q": "Do you store my numbers?",
  "pricing.faq6.a": "No. Each answer is fetched from your accounts when you ask and not kept. Billing details and invoices live in Stripe; we store the subscription state and nothing about your data.",
  // ADR-0062: clients included, no bigger tier, more than three - a request on /clients/more (open to everyone, BL-196).
  "pricing.faqClients.q": "Can I use V-Kit for several clients?",
  "pricing.faqClients.a": "Yes. Up to {count} clients are included in the same price: each client is its own group of sign-ins and keys, and in the chat you name the client you mean. There is no per-client charge and no bigger plan. Need more than {count}? Tell us.",
  "pricing.faqClients.more": "Tell us how many you need",
  "pricing.guideLine":"One plan with everything: €19.99 a month or €199 a year, VAT included where it applies; the first 30 days are free, no card.",

  // /subscribe - the mockup «Checkout» as the step before Stripe's hosted page (ADR-0046).
  "subscribe.metaTitle": "Subscribe · V-Kit",
  "subscribe.secure": "Secure checkout · card details go to Stripe, never to us",
  "subscribe.h1": "Subscribe",
  "subscribe.trialLine": "Your trial has {days} days left. Subscribe now and keep them - the first charge is on {date}.",
  "subscribe.endingLine": "Your trial ends on {date}. That is too close to keep the days, so the first charge is today.",
  "subscribe.giftLine": "You have a gift subscription until {date}. It does not turn into a paid plan by itself: subscribe to keep access after it. Subscribe now and nothing is charged until {date}; the first charge is that day.",
  "subscribe.giftShortLine": "Your gift ends on {date}, in less than two days. The paid plan starts as soon as you subscribe and the remaining gift time is replaced - the first charge is today.",
  "subscribe.expiredLine": "Your trial has ended. The first charge is today; the assistant starts answering again the moment it goes through.",
  "subscribe.period": "Billing period",
  "subscribe.monthlyHint": "Billed every month · cancel any time",
  "subscribe.yearlyHint": "€16.58 a month · save 17%",
  "subscribe.nextStep": "Next step: Stripe's secure page. Stripe sells the subscription as merchant of record: it asks for the payment method - card, Apple Pay, Google Pay or another one it offers in your country - works out the tax and sends the receipt.",
  "subscribe.continue": "Continue to secure checkout",
  "subscribe.going": "Opening Stripe…",
  "subscribe.terms": "By subscribing you agree to the {terms} and ask for the service to start right away, so the statutory withdrawal right ends where the law allows. Instead, any charge can be refunded within 60 days, under Stripe's rules.",
  "subscribe.termsLink": "Terms",
  "subscribe.summary": "Summary",
  "subscribe.product": "V-Kit · Marketing Kit",
  "subscribe.included": "Everything included · {period}",
  "subscribe.vat": "VAT",
  "subscribe.vatNext": "included where it applies; Stripe works it out at the next step",
  "subscribe.dueOn": "Due on {date}",
  "subscribe.dueToday": "Due today",
  "subscribe.scheduleTrial": "€0.00 today, then {price} {every} from {date}, until you cancel. VAT included where it applies.",
  "subscribe.scheduleNow": "{price} today, then {every}, until you cancel. VAT included where it applies.",
  "subscribe.everyMonth": "every month",
  "subscribe.everyYear": "every year",
  "subscribe.cancelNote": "Cancel in one click from Account. Access to the end of the paid period.",
  "subscribe.invoiceNote": "A receipt and an invoice from Stripe after every charge; the invoices are also in Account.",
  "subscribe.sellerNote": "Seller: {seller}, as {role}. The service is provided by {name} ({legal}), {address}.",
  "subscribe.openSoon": "Subscriptions open soon",
  "subscribe.openSoonText": "Paid plans are not open yet. There is nothing to do now: V-Kit keeps working as it does today.",
  "subscribe.subscribed": "You're subscribed",
  "subscribe.subscribedText": "Your plan, payment method and invoices are in Account.",
  "subscribe.toAccount": "Go to Account",
  "subscribe.comp": "Your access is complimentary",
  "subscribe.compText": "There is nothing to pay. Your plan is shown in Account.",
  "subscribe.tooMany": "Too many checkout attempts in the last hour. Try again later.",
  "subscribe.unavailable": "The payment provider is unavailable right now. Try again later or write to connect@valas.team.",
  "subscribe.failed": "Could not open the checkout. Try again later.",
  "subscribe.loadFailed": "Could not load your plan. Try again later.",

  // Account → Plan (BL-132): the mockup «Account» (tweak plan), every sentence checked against ADR-0045/0046/0047.
  "plan.title": "Plan",
  "plan.included": "Everything included",
  "plan.noteMonthly": "a month · or {yearly} a year",
  "plan.noteYearly": "a year · €16.58 a month",
  "plan.day": "1 day",
  "plan.days": "{n} days",
  "plan.tag.trial": "Trial",
  "plan.tag.trialEnding": "Trial · {left} left",
  "plan.tag.expired": "Trial ended",
  "plan.tag.ended": "Subscription ended",
  "plan.tag.monthly": "Active · monthly",
  "plan.tag.yearly": "Active · yearly",
  "plan.tag.active": "Active",
  "plan.tag.pastDue": "Payment failed",
  "plan.tag.paused": "Paused",
  "plan.tag.cancelled": "Cancelled",
  "plan.tag.comp": "Complimentary",
  "plan.tag.gift": "Gift",
  "plan.tag.giftEnding": "Gift · {left} left",
  "plan.trial.headline": "Free trial - {left} left",
  "plan.trial.headlineDays": "{total} free days - {left} left",
  "plan.trial.body": "Every source and assistant is open. Nothing is charged unless you subscribe; if you do, the remaining days are kept and the first charge is on {date}.",
  "plan.progressLeft": "{left} left",
  "plan.progressStarted": "Started {date}",
  "plan.progressEnds": "Ends {date}",
  "plan.ending.headline": "Your trial ends on {date}",
  "plan.ending.body": "After that the assistant answers “subscription needed” until you subscribe. Connections and Account keep working, and your keys and connections stay until you delete them.",
  "plan.ending.keep": "Subscribe now and keep the remaining days - the first charge is on {date}.",
  "plan.expired.headline": "Your trial ended on {date}",
  "plan.ended.headline": "Your subscription ended on {date}",
  "plan.expired.body": "The assistant is paused; everything you connected is still here, and nothing needs to be reinstalled. Subscribe and it answers again within a minute.",
  "plan.expired.extension": "Need a few more days to decide? Ask the assistant in a chat for 3 more days - it can give them once.",
  "plan.subscribe": "Subscribe",
  "plan.subscribeFree": "Subscribe - €0.00 today",
  "plan.subscribeMonthly": "Subscribe - {price} a month",
  "plan.whatsIncluded": "What's included →",
  "plan.monthly.headline": "Renews {date} for {price}",
  "plan.renews": "Renews {date}",
  "plan.monthly.body": "Switch to yearly and pay {yearly} - the rest of this month is credited.",
  "plan.yearly.headline": "Renews {date} for {price}",
  "plan.yearly.body": "Going back to monthly takes effect at renewal; cancelling keeps access until then.",
  "plan.toYearly": "Switch to yearly · save €40",
  "plan.toYearlyFree": "Switch to yearly · save 17%",
  "plan.monthly.bodyOther": "Switch to yearly and save 17%; the rest of this month is credited.",
  // ADR-0058: the old subscriptions (VALAS the seller, exclusive prices) - the sentence they had; the new ones include the tax.
  "plan.vatExcluded": "VAT is included in the price where it applies.",
  "plan.seller": "Sold by Stripe as merchant of record - the receipts and invoices are Stripe's.",
  "plan.toMonthly": "Switch to monthly at renewal",
  "plan.toYearlyConfirm": "You pay {yearly} now, minus what is left of this month.",
  "plan.toYearlyConfirmOther": "You pay the yearly price now, minus what is left of this month; the invoice shows it in your currency.",
  "plan.toMonthlyConfirm": "Monthly from {date}; the year you paid for runs to the end.",
  "plan.switchYes": "Yes, switch",
  "plan.keep": "Keep",
  "plan.invoices": "Invoices ↓",
  "plan.cancel": "Cancel subscription…",
  "plan.cancelConfirm": "Access stays until {date}, no further charges.",
  "plan.cancelConfirmNoDate": "Access stays until the end of the paid period, no further charges.",
  "plan.cancelYes": "Yes, cancel",
  "plan.pastDue.headline": "Your last charge didn't go through",
  "plan.pastDue.body": "Stripe retries until {date} and the assistant keeps answering meanwhile. Update the card or ask your bank - after {date} the assistant pauses.",
  "plan.pastDue.bodyNoDate": "Stripe retries for up to 7 days and the assistant keeps answering meanwhile. Update the card or ask your bank - after that the assistant pauses.",
  "plan.changePortal": "Change plan in the customer portal",
  "plan.updatePayment": "Update payment method",
  "plan.paused.headline": "Paused - update your card",
  "plan.paused.body": "Stripe's retries ran out, so the assistant is paused; everything you connected is still here, and nothing needs to be reinstalled. Update the card in Stripe and pay the open invoice - it answers again right after.",
  "plan.updateCard": "Update card",
  "plan.cancelled.headline": "Access is paid until {date}",
  "plan.cancelled.body": "It will not renew. No further charges. Change your mind any time before {date} and the subscription continues as if nothing happened; after that, subscribing again starts a new subscription. Your keys and connections stay either way.",
  "plan.resume": "Turn renewal back on",
  "plan.comp.headline": "Your access is complimentary",
  "plan.comp.body": "Every source and assistant is open, and there is nothing to pay.",
  "plan.comp.until": "Complimentary until {date}.",
  "plan.gift.headline": "Gift subscription until {date}",
  "plan.gift.body": "Every source and assistant is open - nothing to pay while the gift lasts. After {date} the assistant pauses unless you subscribe; your keys and connections stay either way.",
  "plan.gift.startsOn": "The gift does not renew by itself. To keep access after {date}, subscribe - you can do it now: nothing is charged until {date}.",
  "plan.gift.startsNow": "The gift ends in less than two days, so the paid plan starts as soon as you subscribe - the remaining gift time is replaced.",
  "plan.gift.subscribe": "Subscribe - first charge {date}",
  "plan.gift.subscribeNow": "Subscribe - {price} a month",
  "plan.gift.label": "Have a gift code?",
  "plan.gift.placeholder": "VK-XXXX-XXXX-XXXX",
  "plan.gift.redeem": "Redeem",
  "plan.gift.redeeming": "Redeeming…",
  "plan.gift.hint": "One code, one use. The gift starts right away and replaces the trial.",
  "plan.gift.empty": "Enter your gift code.",
  "plan.gift.err.invalid": "This gift code is not valid.",
  "plan.gift.err.giftActive": "You already have a gift subscription. A new code can be used after it ends.",
  "plan.gift.err.hasSubscription": "You already have a subscription - a gift code can only be used without one.",
  "plan.gift.err.complimentary": "Your access is complimentary - you do not need a gift code.",
  "plan.gift.err.billingOff": "Subscriptions are not open yet, so there is nothing to redeem.",
  "plan.gift.err.tooMany": "Too many attempts. Try again in a few minutes.",
  "plan.gift.err.failed": "Something went wrong. Try again later.",
  "plan.waiting": "Waiting for Stripe…",
  "plan.slow": "Stripe hasn't confirmed it yet - refresh this page in a minute.",
  "plan.switchedMonthly": "Done - monthly from {date}.",
  "plan.loadFailed": "Could not load your plan. Try again later.",
  "plan.err.noSubscription": "There is no subscription to change.",
  "plan.err.alreadyCancelled": "The subscription is already cancelled.",
  "plan.err.notCancelled": "The subscription is not cancelled.",
  "plan.err.samePeriod": "You are already on that billing period.",
  "plan.err.cannotSwitch": "The billing period can be switched only on an active subscription.",
  "plan.err.noBillingAccount": "There is no payment account yet - it appears with the first checkout.",
  "plan.err.unavailable": "The payment provider is unavailable right now. Try again in a minute or write to connect@valas.team.",
  "plan.err.failed": "Something went wrong. Try again later.",
  "plan.checkout.waiting": "Payment received - waiting for Stripe…",
  "plan.checkout.done": "Thanks - you're subscribed",
  "plan.checkout.doneText": "Keep the plugin and your connections as they are - nothing needs to be reinstalled.",
  "plan.checkout.slow": "Payment received, activating… If this takes longer than a minute, write to connect@valas.team",
  "plan.menu": "Plan",
  "plan.menu.trial": "trial · {left} left",
  "plan.menu.expired": "trial ended",
  "plan.menu.ended": "subscription ended",
  "plan.menu.monthly": "monthly · renews {date}",
  "plan.menu.yearly": "yearly · renews {date}",
  "plan.menu.pastDue": "payment failed",
  "plan.menu.paused": "paused",
  "plan.menu.cancelled": "access until {date}",
  "plan.menu.comp": "complimentary",
  "plan.menu.gift": "gift until {date}",
  // /preview answered 402 (ADR-0045: blocked like the tools): the server's message and one button.
  "plan.paywall.required": "Subscription needed",
  "plan.paywall.paused": "Payment paused",
  "plan.paywall.fallback": "Your V-Kit access is paused. Subscribe and the numbers show again - nothing needs to be reinstalled.",
  "plan.paywall.nothingRead": "Nothing was read from your accounts.",

  // Account → Billing (BL-132): only with a Stripe customer; the details and the card are Stripe's (ADR-0046).
  "billing.title": "Billing",
  "billing.lead": "Who the invoices are issued to, how you pay, and every invoice so far. Changes apply from the next invoice.",
  "billing.details": "Billing details",
  "billing.detailsText": "The name, address and VAT number on your invoices are kept by Stripe.",
  "billing.edit": "Edit in Stripe",
  "billing.method": "Payment method",
  "billing.methodText": "Your card or other payment method is kept by Stripe - we never see it.",
  "billing.update": "Update in Stripe",
  "billing.invoices": "Invoices",
  "billing.colDate": "Date",
  "billing.colNumber": "Invoice",
  "billing.colAmount": "Amount",
  "billing.colStatus": "Status",
  "billing.pdf": "Invoice PDF",
  "billing.receipt": "Receipt",
  "billing.pay": "Pay online",
  "billing.status.paid": "Paid",
  "billing.status.open": "Open · not paid yet",
  "billing.status.void": "Void",
  "billing.status.uncollectible": "Uncollectible",
  "billing.loading": "Loading invoices…",
  "billing.none": "No invoices yet - the first one comes with the first charge.",
  "billing.failed": "Could not load invoices - {link}",
  "billing.failedLink": "open them in Stripe",

  // Home strip (BL-132): the mockup «Home» (tweak trial), only in trial-ending, expired, past-due, paused.
  "strip.ending.title": "Trial · {left} left.",
  "strip.ending.body": "Subscribe now and keep them - nothing is charged until {date}.",
  "strip.ending.bodyShort": "Subscribe now and the assistant keeps answering after {date}.",
  "strip.giftEnding.title": "Gift · {left} left.",
  "strip.giftEnding.body": "Subscribe now and keep access - nothing is charged until {date}.",
  "strip.expired.title": "Your trial ended {date}.",
  "strip.ended.title": "Your subscription ended {date}.",
  "strip.expired.body": "The assistant is paused; everything you connected is still here - subscribe and it answers again, nothing to reinstall.",
  "strip.pastDue.title": "Payment didn't go through.",
  "strip.pastDue.body": "Stripe retries until {date} and the assistant keeps answering meanwhile - update the card when you can.",
  "strip.pastDue.bodyNoDate": "Stripe retries for up to 7 days and the assistant keeps answering meanwhile - update the card when you can.",
  "strip.paused.title": "Payment paused.",
  "strip.paused.body": "The assistant is paused; your connections are intact - update the card and it answers again.",
  "strip.price": "{price} a month, everything",
  "strip.subscribe": "Subscribe",
  "strip.updateCard": "Update card",
  "brand.logoAlt": "V-Kit logo",

  "size.b": "B",
  "size.kb": "KB",
  "meta.title.adminFeedback": "Feedback · V-Kit",
  "admin.feedback.title": "Feedback",
  "admin.feedback.lead": "The last 200 messages from chats and the site, newest first. Your reply is shown to the user once, with the next answer of the assistant, and in the Feedback block on the site.",
  "admin.feedback.filter.needs_answer": "Needs answer ({n})",
  "admin.feedback.filter.answered": "Answered ({n})",
  "admin.feedback.filter.all": "All ({n})",
  "admin.feedback.emptyNeeds": "Nothing waits for an answer.",
  "admin.feedback.emptyAnswered": "No answered messages yet.",
  "admin.feedback.chip.answered": "answered",
  "admin.feedback.chip.delivered": "delivered via {via} · {date}",
  "admin.feedback.chip.deliveredNoVia": "delivered · {date}",
  "admin.feedback.chip.userAnswered": "user answered {date}",
  "admin.feedback.author.developer": "You",
  "admin.feedback.author.user": "User",
  "admin.feedback.replyLabel": "Reply",
  "admin.feedback.replyPlaceholder": "Your reply to the user",
  "admin.feedback.replyCount": "{n} / {max}",
  "admin.feedback.replyButton": "Reply",
  "admin.feedback.sending": "Sending…",
  "admin.feedback.delete": "Delete",
  "admin.feedback.deleteConfirm": "Delete this reply? The user has not seen it yet, and it will not be shown.",
  "admin.feedback.error.invalid": "The reply must be 1 to 5000 characters.",
  "admin.feedback.error.notFound": "The message no longer exists (it may have been removed after 28 days).",
  "admin.feedback.error.notDeletable": "This reply has already been shown to the user and can no longer be deleted.",
  "admin.feedback.error.generic": "Could not save the change. Try again.",
  "admin.feedback.notFound": "Not found",
  "admin.feedback.failed": "Could not load the messages.",
  "admin.feedback.billingLink": "Plans and billing",
  "admin.feedback.empty": "No messages yet.",
  "admin.feedback.context": "Technical context",
  "admin.feedback.open": "Open full size",
  "admin.feedback.screenshot": "Screenshot",
  "meta.title.adminBilling": "Plans and billing · V-Kit",
  "admin.billing.title": "Plans and billing",
  "admin.billing.lead": "Every user with the state of the plan, the numbers from our own tables, and the support actions: refund, cancel, trial, free months. The state of a subscription changes only when Stripe confirms.",
  "admin.billing.stripeInitiated": "Some refunds and cancellations come from Stripe itself, not from this page. Stripe is the merchant of record for Managed Payments sales: it can refund a payment on its own, cancel a subscription under the cooling-off rules, or cancel it and delete the buyer's data on request. These events arrive as ordinary webhooks and move the plan to cancelled or expired without any action here. Events marked «From Stripe» are known not to be started by us.",
  "admin.billing.tile.external": "Started outside this page, 30 days",
  "admin.billing.second.title": "Second live subscription",
  "admin.billing.second.note": "A user paid twice. The first subscription was kept; the new one is cancelled and its payment refunded unless the line below says a step is left.",
  "admin.billing.second.line": "{date} · {email}: {error}",
  "admin.billing.events.title": "Recent events",
  "admin.billing.events.col.time": "Received",
  "admin.billing.events.col.type": "Event",
  "admin.billing.events.col.user": "User",
  "admin.billing.events.col.deal": "Deal",
  "admin.billing.events.col.initiator": "Started by",
  "admin.billing.deal.managed": "Stripe sells",
  "admin.billing.deal.own": "VALAS sells",
  "admin.billing.deal.unknown": "-",
  "admin.billing.initiator.ours": "Us",
  "admin.billing.initiator.external": "From Stripe",
  "admin.billing.managedBadge": "Managed Payments",
  "admin.billing.notFound": "Not found",
  "admin.billing.failed": "Could not load the data.",
  "admin.billing.feedbackLink": "Feedback",
  "admin.billing.stripeTest": "Stripe test mode",
  "admin.billing.stripeLive": "Stripe live mode",
  "admin.billing.tile.users": "Users",
  "admin.billing.tile.mrr": "MRR",
  "admin.billing.tile.mrrNote": "{monthly} monthly + {yearly} yearly, active only, at list price",
  "admin.billing.tile.new": "New subscriptions, 30 days",
  "admin.billing.tile.cancelled": "Cancellations, 30 days",
  "admin.billing.tile.refunds": "Refunds, 30 days",
  "admin.billing.tile.refundsNote": "Refunded: {amount}",
  "admin.billing.tile.revenue": "Revenue, 30 days",
  "admin.billing.tile.failed": "Failed payments, 30 days",
  "admin.billing.tile.trials": "Trials ending in 7 days",
  "admin.billing.trialsLine": "{email} - {date}",
  "admin.billing.tile.codes": "Gift codes created",
  "admin.billing.tile.codesNote": "{redeemed} redeemed · {unused} unused · {revoked} revoked · {expired} expired. Codes are made with the console utility.",
  "admin.billing.giftUntil": "gift until {date}",
  "admin.billing.statesTitle": "Users by state",
  "admin.billing.state.trial": "Trial",
  "admin.billing.state.trial-ending": "Trial ending",
  "admin.billing.state.expired": "Expired",
  "admin.billing.state.active": "Active",
  "admin.billing.state.past-due": "Past due",
  "admin.billing.state.paused": "Paused",
  "admin.billing.state.cancelled": "Cancelled",
  "admin.billing.state.comp": "Complimentary",
  "admin.billing.state.gift": "Gift",
  "admin.billing.state.off": "Billing off",
  "admin.billing.tab.users": "Users",
  "admin.billing.tab.lists": "Lists",
  "admin.billing.search": "Search by e-mail",
  "admin.billing.searchButton": "Search",
  "admin.billing.allStates": "All states",
  "admin.billing.sort": "Sort",
  "admin.billing.sort.created": "Joined",
  "admin.billing.sort.state": "State",
  "admin.billing.sort.email": "E-mail",
  "admin.billing.sort.ends": "Period end",
  "admin.billing.sort.last": "Last request",
  "admin.billing.csv": "Export CSV",
  "admin.billing.col.email": "E-mail",
  "admin.billing.col.state": "State",
  "admin.billing.col.period": "Period",
  "admin.billing.col.ends": "Ends",
  "admin.billing.col.stripe": "Stripe",
  "admin.billing.col.lists": "Lists",
  "admin.billing.col.joined": "Joined",
  "admin.billing.noUsers": "No users match.",
  "admin.billing.page": "{from}–{to} of {total}",
  "admin.billing.prev": "Prev",
  "admin.billing.next": "Next",
  "admin.billing.col.lastPayment": "Last payment",
  "admin.billing.refundedTotal": "refunded {amount}",
  "admin.billing.manage": "Manage",
  "admin.billing.close": "Close",
  "admin.billing.openStripe": "Open in Stripe",
  "admin.billing.period.monthly": "monthly",
  "admin.billing.period.yearly": "yearly",
  "admin.billing.cancelsAtEnd": "ends with the period",
  "admin.billing.list.admin": "admin",
  "admin.billing.list.comp": "comp",
  "admin.billing.list.compUntil": "comp until {date}",
  "admin.billing.list.pilot": "pilot",
  "admin.billing.panel.title": "Actions for {email}",
  "admin.billing.action.refund": "Refund",
  "admin.billing.action.cancel": "Cancel",
  "admin.billing.action.trial": "Trial",
  "admin.billing.action.extend": "Free months",
  "admin.billing.refund.amount": "Amount (empty - the whole last payment)",
  "admin.billing.refund.reason": "Reason",
  "admin.billing.refund.reason.requested_by_customer": "Requested by the customer",
  "admin.billing.refund.reason.duplicate": "Duplicate",
  "admin.billing.refund.reason.fraudulent": "Fraudulent",
  "admin.billing.refund.note": "Note (optional)",
  "admin.billing.refund.last": "Last payment: {amount}",
  "admin.billing.refund.noCustomer": "No Stripe customer: nothing to refund.",
  "admin.billing.cancel.now": "Now - the access ends at once",
  "admin.billing.cancel.periodEnd": "At the end of the paid period",
  "admin.billing.cancel.withRefund": "Refund the last payment",
  "admin.billing.cancel.noSubscription": "No live subscription to cancel.",
  "admin.billing.trial.endsAt": "Trial ends on",
  "admin.billing.trial.current": "Now: {date}",
  "admin.billing.trial.hasSubscription": "A subscription exists: the trial end no longer matters.",
  "admin.billing.extend.months": "Free months",
  "admin.billing.extend.note": "Complimentary access until the end date: our entitlement only, Stripe is not called. A paying subscriber is still invoiced - for them use a 100 % coupon in the Stripe Dashboard.",
  "admin.billing.review": "Continue",
  "admin.billing.back": "Back",
  "admin.billing.confirmLead": "Confirm by pressing the button with the words of the action:",
  "admin.billing.phrase.refund": "Refund {amount} to {email}",
  "admin.billing.phrase.refundLast": "Refund the last payment to {email}",
  "admin.billing.phrase.cancelNow": "Cancel {email} now",
  "admin.billing.phrase.cancelNowRefund": "Cancel {email} now and refund the last payment",
  "admin.billing.phrase.cancelEnd": "Cancel {email} at the end of the period",
  "admin.billing.phrase.trial": "End the trial of {email} on {date}",
  "admin.billing.phrase.extend": "Give {email} {n} free month(s)",
  "admin.billing.working": "Working…",
  "admin.billing.pending": "Sent to Stripe - waiting for Stripe to confirm. The row updates when its webhook lands.",
  "admin.billing.done": "Done.",
  "admin.billing.doneRefund": "Refunded {amount} (refund {id}, {status}).",
  "admin.billing.doneExtend": "Complimentary until {date}.",
  "admin.billing.openDashboard": "Open the customer in the Stripe Dashboard",
  "admin.billing.error": "Not done: {message}",
  "admin.billing.lists.comp": "Complimentary access",
  "admin.billing.lists.compLead": "Free access without a card: friends and the team. An end date makes it a temporary gift.",
  "admin.billing.lists.pilot": "Pilot testers",
  "admin.billing.lists.pilotLead": "In pilot mode the billing rules apply only to these e-mails.",
  "admin.billing.lists.env": "From .env (read-only)",
  "admin.billing.lists.empty": "Nobody added here yet.",
  "admin.billing.lists.email": "E-mail",
  "admin.billing.lists.until": "Until (empty - no end)",
  "admin.billing.lists.note": "Note",
  "admin.billing.lists.addedBy": "Added by",
  "admin.billing.lists.add": "Add",
  "admin.billing.lists.remove": "Remove",
  "admin.billing.lists.expired": "expired",
  "admin.billing.lists.noEnd": "no end",
  "feedback.title": "Feedback",
  "feedback.lead": "Tell us what is missing or broken - the developer reads every message.",
  "feedback.textLabel": "Your message",
  "feedback.placeholder": "What did you try to do, and what went wrong?",
  "feedback.counter": "{n} / {max}",
  "feedback.pictureLabel": "Picture (optional)",
  "feedback.addPicture": "Add a picture",
  "feedback.pictureHint": "PNG, JPEG or WebP, up to 5 MB.",
  "feedback.preview": "Preview of the chosen picture",
  "feedback.remove": "Remove",
  "feedback.send": "Send to the developer",
  "feedback.sending": "Sending…",
  "feedback.thanks": "Thanks - the developer will read it",
  "feedback.errTooMany": "Too many messages today - please try again tomorrow.",
  "feedback.errTooLarge": "The picture is larger than 5 MB.",
  "feedback.errType": "Only PNG, JPEG or WebP pictures.",
  "feedback.errSignIn": "Please sign in again.",
  "feedback.errOther": "Could not send - please try again.",
  "feedback.attachLead": "Add a screenshot to the message you sent from the chat.",
  "feedback.attach": "Attach screenshot",
  "feedback.attached": "Screenshot attached - thanks!",
  "feedback.attachNotFound": "This message was not found in your account.",
  "feedback.attachFull": "This message already has 3 pictures.",
  "feedback.writeNew": "Write a new message",
  "feedback.replied": "Developer replied",
  "feedback.repliedCount": "Developer replied · {n}",
  "feedback.newReply": "New",
  "feedback.notesTitle": "Your notes",
  "feedback.kind.bug": "Problem",
  "feedback.kind.idea": "Idea",
  "feedback.kind.other": "Note",
  "feedback.replyCount": "{n} in thread",
  "feedback.author.you": "You",
  "feedback.author.developer": "Developer",
  "feedback.author.user": "You",
  "feedback.answerLabel": "Answer",
  "feedback.answerPlaceholder": "Your answer to the developer",
  "feedback.answerSend": "Send answer",
  "feedback.loading": "Loading…",
  "feedback.loadFailed": "Could not load this thread.",
  "size.mb": "MB",
  "size.gb": "GB",
} as const;

export type Dict = Record<keyof typeof en, string>;
